import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ElevationProfile } from '@freedom/elevation';
import type { ObstacleFeature } from '@freedom/elevation';
import { coverageBoundary as createCoverageBoundary, destinationPoint, haversineDistanceM, initialBearingDeg } from '@freedom/geospatial';
import { analyzeLineOfSight, calculateInterferenceMatrix, calculateLinkBudget, calculatePlannedInterference, type EquipmentRecommendation, type HeightOptimizationMode, type InterferenceSector, type LinkBudgetResult } from '@freedom/rf-engine';
import type { AntennaModelRecord, ClientBatchAnalysis, Coordinates, CoverageJobSnapshot, DesignSite, ManualObstacle, ProjectViewState, RadioDeviceRecord, SiteType } from '@freedom/shared';
import {
  Antenna, Bell, Bug, ChevronDown, CircleDot, CloudRain, CloudSun, Crosshair, Database, Ellipsis, Eye, EyeOff, Gauge, Layers3,
  LocateFixed, MapPinPlus, Minus, MousePointer2, Plus, RadioTower,
  Network, Redo2, Ruler, Save, Settings2, ShieldCheck, SlidersHorizontal, Sparkles, Trash2, Undo2, Users, X,
} from 'lucide-react';
import { MapWorkspace, type MapCommand, type MapContextPoint, type MapRadioEndpoint, type MapScreenPoint, type MapToolId } from './MapWorkspace';
import { AuthDialog, consumeAuthUrlAction, type AuthUrlAction } from './AuthDialog';
import { ProjectDialog } from './ProjectDialog';
import type { LinkEngineeringAnalysis, ProjectSummary, SessionUser } from './api';
import { api, ApiError, buildLinkAnalysisRequest, isReusableLinkAnalysis, linkPathAnalysisRequestKey } from './api';
import { SearchBox } from './SearchBox';
import { ObstacleDialog } from './ObstacleDialog';
import { PtPInspector } from './PtPInspector';
import { SitePopup, type SitePopupLink } from './SitePopup';
import { ProfileCanvas, type ProfileHoverPosition } from './ProfileCanvas';
import { EquipmentDialog } from './EquipmentDialog';
import { renderCoverageOverlay, renderInterferenceOverlay, type CoverageOverlay } from './coverage-overlay';
import { TopologyDialog } from './TopologyDialog';
import { AdminDialog } from './AdminDialog';
import { FeedbackDialog } from './FeedbackDialog';
import { hydrateProjectViewState, hydrateProjectWorkspace, serializeProjectWorkspace } from './project-workspace';
import { averageGroupDistanceM, defaultDisplaySettings, formatDistance, moveEndpointAlongBearing, normalizeExplicitLinkLossDb, type DisplaySettings, type PtMpGroupModel, type RadioLinkKind, type RadioLinkModel } from './radio-workspace';
import { createClientId } from './client-id';
import { draftProjectRecords, loadWorkspaceDraft, saveWorkspaceDraft, type DraftCoverageConfig, type WorkspaceDraftPayload } from './workspace-draft';
import { createConsentAwareBrowserTerrainProfileCache, terrainProfileCacheKey } from './terrain-profile-cache';
import { cancelMapTool, nextMapTool } from './map-tool-state';
import type { EquipmentSelectionContext } from './equipment-catalog-view';
import { consentAllows, readCookieConsent } from './cookie-consent';
import {
  antennaPatchForRadioChange,
  applyAutoEquipmentRecommendations,
  automaticEquipmentTargetSiteIds,
  clampSiteTxPowerDbm,
  displayEquipmentName,
  maximumSiteTxPowerDbm,
  mutuallySupportedChannelWidths,
  patchLinkChannelWidth,
  siteAntennaCompatibilityReason,
  siteAntennaCompatibilityWarning,
} from './radio-parameters';
import { allocatePtMpStationIdentity, allocateStationNames } from './station-naming';
import { profileMapCoordinateAtDistance } from './profile-map-coordinate';
import { PtPCapacityTrack } from './PtPCapacityTrack';
import { calculatePtPCapacity, formatCapacityMbps } from './ptp-capacity';

interface MapToolDefinition {
  id: MapToolId;
  label: string;
  description: string;
  icon: typeof MousePointer2;
}

interface ProfileMapHoverState extends ProfileHoverPosition {
  linkKey: string;
  workspaceKey: string;
}

const selectTool: MapToolDefinition = { id: 'select', label: 'Выбор', description: 'Выбрать или переместить объект на карте', icon: MousePointer2 };

const creationTools: MapToolDefinition[] = [
  { id: 'site', label: 'Добавить объект', description: 'Разместить универсальную точку проекта', icon: MapPinPlus },
  { id: 'base_station', label: 'Добавить базовую станцию', description: 'Разместить отдельную AP / базовую станцию', icon: RadioTower },
  { id: 'client', label: 'Добавить клиента', description: 'Разместить клиентскую радиостанцию', icon: Users },
  { id: 'ptp', label: 'Создать PtP', description: 'Указать два новых или существующих endpoint', icon: Antenna },
  { id: 'ptmp', label: 'Создать PtMP и покрытие', description: 'Разместить AP, клиентов и исходный сектор', icon: CircleDot },
];

const secondaryTools: MapToolDefinition[] = [
  { id: 'obstacle', label: 'Добавить препятствие', description: 'Указать ручное препятствие на трассе', icon: Layers3 },
  { id: 'measure', label: 'Измерить расстояние', description: 'Последовательно указать две точки; режим повторяемый', icon: Ruler },
];

function ControlTooltip({ label, description, rail = false }: { label: string; description?: string; rail?: boolean }): React.JSX.Element {
  return <span className={rail ? 'map-control-tooltip rail-tooltip' : 'map-control-tooltip'} role="tooltip"><strong>{label}</strong>{description ? <small>{description}</small> : null}</span>;
}

function MapToolButton({ definition, active, rail = false, onSelect }: { definition: MapToolDefinition; active: boolean; rail?: boolean; onSelect(tool: MapToolId): void }): React.JSX.Element {
  const Icon = definition.icon;
  return <button className={`${rail ? 'rail-action' : 'map-tool-button'}${active ? ' active' : ''}`} type="button" data-map-tool={definition.id} aria-label={definition.label} aria-pressed={active} onClick={(event) => { event.stopPropagation(); onSelect(definition.id); }}><Icon size={rail ? 19 : 17} strokeWidth={1.8} /><ControlTooltip label={definition.label} description={definition.description} rail={rail} /></button>;
}

const initialSites: DesignSite[] = [];
const initialRadioLinks: RadioLinkModel[] = [];
const storedCookieConsent = typeof document === 'undefined' ? null : readCookieConsent(document.cookie);
const initialAuthUrlAction = consumeAuthUrlAction();
const terrainProfileCache = createConsentAwareBrowserTerrainProfileCache({
  persistenceAllowed: consentAllows(storedCookieConsent, 'preferences'),
});
export async function applyTerrainProfilePersistencePreference(enabled: boolean): Promise<void> {
  await terrainProfileCache.setPersistenceEnabled(enabled);
}
const ANALYSIS_CACHE_TTL_MS = 10 * 60_000;
const ANALYSIS_CACHE_LIMIT = 32;

interface CachedLinkAnalysis { expiresAt: number; analysis: LinkEngineeringAnalysis }

function readCachedLinkAnalysis(cache: Map<string, CachedLinkAnalysis>, key: string): LinkEngineeringAnalysis | null {
  const entry = cache.get(key);
  if (!entry || entry.expiresAt <= Date.now()) { if (entry) cache.delete(key); return null; }
  cache.delete(key);
  cache.set(key, entry);
  return entry.analysis;
}

function cacheLinkAnalysis(cache: Map<string, CachedLinkAnalysis>, key: string, analysis: LinkEngineeringAnalysis): void {
  cache.delete(key);
  while (cache.size >= ANALYSIS_CACHE_LIMIT) {
    const oldest = cache.keys().next().value as string | undefined;
    if (!oldest) break;
    cache.delete(oldest);
  }
  cache.set(key, { expiresAt: Date.now() + ANALYSIS_CACHE_TTL_MS, analysis });
}

function directionalLinkBudget(transmitter: DesignSite, receiver: DesignSite, distanceKm: number, rainRateMmPerHour: number, additionalLossDb: number): LinkBudgetResult {
  const performanceProfile = receiver.radioPerformanceProfiles?.find((profile) => profile.channelWidthMHz === transmitter.channelWidthMHz)
    ?? transmitter.radioPerformanceProfiles?.find((profile) => profile.channelWidthMHz === transmitter.channelWidthMHz);
  const throughputLimits = [transmitter.radioMaxThroughputMbps, receiver.radioMaxThroughputMbps]
    .filter((value): value is number => value !== undefined && value !== null && Number.isFinite(value) && value > 0);
  const maximumThroughputMbps = throughputLimits.length > 0 ? Math.min(...throughputLimits) : undefined;
  const polarization = transmitter.radioPolarization === 'vertical' || transmitter.radioPolarization === 'circular' ? transmitter.radioPolarization : 'horizontal';
  return calculateLinkBudget({
    frequencyGHz: transmitter.frequencyGHz,
    distanceKm,
    channelWidthMHz: transmitter.channelWidthMHz,
    txPowerDbm: transmitter.txPowerDbm,
    txAntennaGainDbi: transmitter.antennaGainDbi,
    rxAntennaGainDbi: receiver.antennaGainDbi,
    txCableLossDb: transmitter.cableLossDb,
    rxCableLossDb: receiver.cableLossDb,
    additionalLossDb,
    ...(receiver.radioReceiverSensitivityDbm === undefined || receiver.radioReceiverSensitivityDbm === null ? {} : { receiverSensitivityDbm: receiver.radioReceiverSensitivityDbm }),
    noiseFigureDb: receiver.noiseFigureDb,
    ambientNoiseFloorDbm: receiver.ambientNoiseFloorDbm,
    rainRateMmPerHour,
    polarization,
    ...(performanceProfile ? { performanceProfile } : {}),
    ...(maximumThroughputMbps === undefined ? {} : { maximumThroughputMbps }),
  });
}

interface CoverageConfig { maxCoverageRadiusM: number; azimuthDeg: number; mechanicalTiltDeg: number; electricalTiltDeg: number; horizontalBeamwidthDeg: number; verticalBeamwidthDeg: number }
const initialCoverageConfig: CoverageConfig = { maxCoverageRadiusM: 5_000, azimuthDeg: 90, mechanicalTiltDeg: 0, electricalTiltDeg: 2, horizontalBeamwidthDeg: 90, verticalBeamwidthDeg: 8 };
const initialMapViewState: ProjectViewState = { center: { lat: 20, lon: 0 }, zoom: 2.2, pitch: 0, bearing: 0, activeLayers: ['detected_obstacles'], selectedObjectId: null };

function geolocationZoom(accuracyM: number): number {
  if (!Number.isFinite(accuracyM) || accuracyM > 10_000) return 7;
  if (accuracyM > 1_000) return 10;
  if (accuracyM > 100) return 12;
  return 14;
}

function initialViewIsUntouched(viewState: ProjectViewState): boolean {
  return Math.abs(viewState.center.lat - initialMapViewState.center.lat) < 1e-7
    && Math.abs(viewState.center.lon - initialMapViewState.center.lon) < 1e-7
    && Math.abs(viewState.zoom - initialMapViewState.zoom) < 1e-4;
}

function hydrateDraftCoverageConfig(input: DraftCoverageConfig): CoverageConfig {
  const finite = (value: number, fallback: number): number => Number.isFinite(value) ? value : fallback;
  return {
    maxCoverageRadiusM: Math.max(100, Math.min(100_000, finite(input.maxCoverageRadiusM, initialCoverageConfig.maxCoverageRadiusM))),
    azimuthDeg: ((finite(input.azimuthDeg, initialCoverageConfig.azimuthDeg) % 360) + 360) % 360,
    mechanicalTiltDeg: Math.max(-45, Math.min(45, finite(input.mechanicalTiltDeg, initialCoverageConfig.mechanicalTiltDeg))),
    electricalTiltDeg: Math.max(-45, Math.min(45, finite(input.electricalTiltDeg, initialCoverageConfig.electricalTiltDeg))),
    horizontalBeamwidthDeg: Math.max(1, Math.min(360, finite(input.horizontalBeamwidthDeg, initialCoverageConfig.horizontalBeamwidthDeg))),
    verticalBeamwidthDeg: Math.max(1, Math.min(180, finite(input.verticalBeamwidthDeg, initialCoverageConfig.verticalBeamwidthDeg))),
  };
}

interface WorkspaceSnapshot {
  sites: DesignSite[];
  obstacles: ManualObstacle[];
  linkEndpointIds: [string, string];
  coverageConfig: CoverageConfig;
  selectedSiteId: string | null;
  radioLinks: RadioLinkModel[];
  ptmpGroups: PtMpGroupModel[];
  selectedRadioLinkId: string | null;
  displaySettings: DisplaySettings;
}

export function App(): React.JSX.Element {
  const [sites, setSites] = useState(initialSites);
  const [linkEndpointIds, setLinkEndpointIds] = useState<[string, string]>(['', '']);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<MapToolId>('select');
  const [radioDraftStartSiteId, setRadioDraftStartSiteId] = useState<string | null>(null);
  const [radioDraftStaged, setRadioDraftStaged] = useState(false);
  const [radioLinks, setRadioLinks] = useState<RadioLinkModel[]>(initialRadioLinks);
  const [ptmpGroups, setPtmpGroups] = useState<PtMpGroupModel[]>([]);
  const [selectedRadioLinkId, setSelectedRadioLinkId] = useState<string | null>(null);
  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>(defaultDisplaySettings);
  const [displayDrawerOpen, setDisplayDrawerOpen] = useState(false);
  const [displayDrawerMode, setDisplayDrawerMode] = useState<'coverage' | 'settings'>('settings');

  const [mapTypeOpen, setMapTypeOpen] = useState(false);
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [siteMoveActive, setSiteMoveActive] = useState(false);
  const [productBusy, setProductBusy] = useState(false);
  const [autoRecommendation, setAutoRecommendation] = useState<EquipmentRecommendation | null>(null);
  const [rainRateMmPerHour, setRainRateMmPerHour] = useState(0);
  const [profileOpen, setProfileOpen] = useState(true);
  const [profileMapHover, setProfileMapHover] = useState<ProfileMapHoverState | null>(null);
  const [contextPoint, setContextPoint] = useState<MapContextPoint | null>(null);
  const [selectedSiteAnchor, setSelectedSiteAnchor] = useState<MapScreenPoint | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(initialAuthUrlAction !== null);
  const [authUrlAction, setAuthUrlAction] = useState<AuthUrlAction | null>(initialAuthUrlAction);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [pendingProject, setPendingProject] = useState<ProjectSummary | null>(null);
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [sessionResolved, setSessionResolved] = useState(false);
  const [draftReadyUserId, setDraftReadyUserId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('Новый проект');
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'dirty' | 'saving' | 'error'>('saved');
  const [saveInFlight, setSaveInFlight] = useState(false);
  const [mapViewState, setMapViewState] = useState<ProjectViewState>(initialMapViewState);
  const [mapResetRevision, setMapResetRevision] = useState(0);
  const [mapCommand, setMapCommand] = useState<MapCommand | null>(null);
  const [focusCoordinates, setFocusCoordinates] = useState<Coordinates | null>(null);
  const [obstacles, setObstacles] = useState<ManualObstacle[]>([]);
  const [detectedObstacles, setDetectedObstacles] = useState<ObstacleFeature[]>([]);
  const [obstacleWarnings, setObstacleWarnings] = useState<string[]>([]);
  const [pendingObstacle, setPendingObstacle] = useState<Coordinates | null>(null);
  const [obstacleSaveError, setObstacleSaveError] = useState<string | null>(null);
  const [elevationProfile, setElevationProfile] = useState<ElevationProfile | null>(null);
  const [terrainCacheSource, setTerrainCacheSource] = useState<'memory' | 'storage' | null>(null);
  const [elevationError, setElevationError] = useState<string | null>(null);
  const [linkAnalysis, setLinkAnalysis] = useState<LinkEngineeringAnalysis | null>(null);
  const [resolvedLinkAnalysisKey, setResolvedLinkAnalysisKey] = useState<string | null>(null);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [equipmentDialogInitialTab, setEquipmentDialogInitialTab] = useState<'radio' | 'antenna'>('radio');
  const [equipmentDialogTargetSiteId, setEquipmentDialogTargetSiteId] = useState<string | null>(null);
  const [equipmentRadios, setEquipmentRadios] = useState<RadioDeviceRecord[]>([]);
  const [equipmentAntennas, setEquipmentAntennas] = useState<AntennaModelRecord[]>([]);
  const [optimizationMode, setOptimizationMode] = useState<HeightOptimizationMode>('b');
  const [optimizationTarget, setOptimizationTarget] = useState<60 | 100>(60);
  const [coverageConfig, setCoverageConfig] = useState<CoverageConfig>(initialCoverageConfig);
  const [coverageJob, setCoverageJob] = useState<CoverageJobSnapshot | null>(null);
  const [coverageOverlay, setCoverageOverlay] = useState<CoverageOverlay | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [clientAnalysis, setClientAnalysis] = useState<ClientBatchAnalysis | null>(null);
  const [clientAnalysisBusy, setClientAnalysisBusy] = useState(false);
  const [clientAnalysisError, setClientAnalysisError] = useState<string | null>(null);
  const [topologyDialogOpen, setTopologyDialogOpen] = useState(false);
  const [interferenceOverlay, setInterferenceOverlay] = useState<CoverageOverlay | null>(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const undoStackRef = useRef<WorkspaceSnapshot[]>([]);
  const redoStackRef = useRef<WorkspaceSnapshot[]>([]);
  const dragHistoryOpenRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const openProjectRequestRef = useRef(0);
  const analysisCacheRef = useRef(new Map<string, CachedLinkAnalysis>());
  const analysisRequestTailRef = useRef<Promise<void>>(Promise.resolve());
  const analysisCacheUserIdRef = useRef<string | null>(null);
  const displayedTerrainKeyRef = useRef<string | null>(null);
  const elevationProfileRef = useRef<ElevationProfile | null>(elevationProfile);
  elevationProfileRef.current = elevationProfile;
  const persistedDisplaySettingsRef = useRef(JSON.stringify(defaultDisplaySettings));
  const [, setHistoryRevision] = useState(0);
  const selectedSite = sites.find((site) => site.id === selectedSiteId) ?? null;
  const selectedRadioLink = radioLinks.find((link) => link.id === selectedRadioLinkId) ?? null;
  const activePtMpGroup = selectedRadioLink?.groupId ? ptmpGroups.find((group) => group.id === selectedRadioLink.groupId) ?? null : null;
  const linkStartSite = sites.find((site) => site.id === linkEndpointIds[0]);
  const linkEndSite = sites.find((site) => site.id === linkEndpointIds[1]);
  const linkSites = useMemo(() => linkStartSite && linkEndSite ? [linkStartSite, linkEndSite] : [], [linkStartSite, linkEndSite]);
  const equipmentSelectionSite = sites.find((site) => site.id === equipmentDialogTargetSiteId) ?? (selectedRadioLink ? linkStartSite ?? null : null);
  const equipmentSelectionContext: EquipmentSelectionContext = !selectedRadioLink
    ? 'catalog'
    : selectedRadioLink.kind === 'ptp'
      ? 'ptp'
      : equipmentSelectionSite?.id === selectedRadioLink.startSiteId ? 'ap' : 'cpe';
  const equipmentTargetSiteIds = !selectedRadioLink
    ? []
    : selectedRadioLink.kind === 'ptp'
      ? linkEndpointIds.filter(Boolean)
      : equipmentDialogTargetSiteId ? [equipmentDialogTargetSiteId] : linkEndpointIds.filter(Boolean);
  const equipmentTargetSites = sites.filter((site) => equipmentTargetSiteIds.includes(site.id));
  const linkTxPowerLimitsDbm = useMemo<[number | null, number | null]>(() => linkStartSite && linkEndSite
    ? [maximumSiteTxPowerDbm(linkStartSite, equipmentRadios), maximumSiteTxPowerDbm(linkEndSite, equipmentRadios)]
    : [null, null], [linkStartSite, linkEndSite, equipmentRadios]);
  const automaticChannelWidthsMHz = useMemo(() => {
    const engineeringReady = equipmentRadios.filter((radio) => radio.isActive
      && radio.maxTxPowerDbm !== null
      && radio.receiverSensitivityDbm !== null
      && radio.maxThroughputMbps !== null);
    if (selectedRadioLink?.kind !== 'ptmp') {
      return [...new Set(engineeringReady.filter((radio) => radio.deviceType === 'ptp-radio').flatMap((radio) => radio.channelWidthsMHz))].sort((left, right) => left - right);
    }
    const apWidths = new Set(engineeringReady.filter((radio) => radio.deviceType === 'ap-radio').flatMap((radio) => radio.channelWidthsMHz));
    const stationWidths = new Set(engineeringReady.filter((radio) => radio.deviceType === 'client-radio').flatMap((radio) => radio.channelWidthsMHz));
    return [...apWidths].filter((width) => stationWidths.has(width)).sort((left, right) => left - right);
  }, [equipmentRadios, selectedRadioLink?.kind]);
  const automaticEquipmentTargetIds = useMemo(
    () => automaticEquipmentTargetSiteIds(selectedRadioLink, ptmpGroups),
    [selectedRadioLink, ptmpGroups],
  );
  const manualChannelWidthsMHz = useMemo(
    () => mutuallySupportedChannelWidths(sites, automaticEquipmentTargetIds),
    [sites, automaticEquipmentTargetIds],
  );
  const linkAnalysisRequest = useMemo(() => linkStartSite && linkEndSite ? buildLinkAnalysisRequest(linkStartSite, linkEndSite, currentProjectId, obstacles, rainRateMmPerHour, selectedRadioLink?.additionalLossDb ?? 0) : null, [linkStartSite, linkEndSite, currentProjectId, obstacles, rainRateMmPerHour, selectedRadioLink?.additionalLossDb]);
  const linkAnalysisKey = useMemo(() => linkAnalysisRequest ? linkPathAnalysisRequestKey(linkAnalysisRequest) : null, [linkAnalysisRequest]);
  const terrainPathKey = useMemo(() => linkStartSite && linkEndSite ? terrainProfileCacheKey({ start: linkStartSite.coordinates, end: linkEndSite.coordinates, sourcePolicy: 'configured-dem-v1', optionsPolicy: 'auto-spacing-max-4000-v1' }) : null, [linkStartSite, linkEndSite]);
  const profileHoverLinkKey = selectedRadioLink ? `${selectedRadioLink.id}:${selectedRadioLink.startSiteId}:${selectedRadioLink.endSiteId}` : null;
  const profileHoverWorkspaceKey = `${currentProjectId ?? 'temporary'}:${mapResetRevision}`;
  const profileHoverCoordinates = useMemo(() => {
    if (!profileOpen || !profileMapHover || !profileHoverLinkKey || profileMapHover.linkKey !== profileHoverLinkKey || profileMapHover.workspaceKey !== profileHoverWorkspaceKey || linkSites.length !== 2) return null;
    return profileMapCoordinateAtDistance(linkSites[0]!.coordinates, linkSites[1]!.coordinates, profileMapHover.distanceM, profileMapHover.totalDistanceM);
  }, [profileOpen, profileMapHover, profileHoverLinkKey, profileHoverWorkspaceKey, linkSites]);
  const changeProfileHover = useCallback((position: ProfileHoverPosition | null): void => {
    if (!position || !profileHoverLinkKey) {
      setProfileMapHover(null);
      return;
    }
    setProfileMapHover((current) => current
      && current.linkKey === profileHoverLinkKey
      && current.workspaceKey === profileHoverWorkspaceKey
      && current.distanceM === position.distanceM
      && current.totalDistanceM === position.totalDistanceM
      ? current
      : { ...position, linkKey: profileHoverLinkKey, workspaceKey: profileHoverWorkspaceKey });
  }, [profileHoverLinkKey, profileHoverWorkspaceKey]);
  const currentScopedLinkAnalysisKey = currentUser && linkAnalysisKey ? `${currentUser.userId}:${linkAnalysisKey}` : null;
  const settledLinkAnalysis = currentScopedLinkAnalysisKey && resolvedLinkAnalysisKey === currentScopedLinkAnalysisKey ? linkAnalysis : null;
  const previewLinkAnalysis = useMemo<LinkEngineeringAnalysis | null>(() => {
    if (!linkAnalysis || !linkAnalysisRequest || !terrainPathKey || displayedTerrainKeyRef.current !== terrainPathKey) return null;
    const profile = linkAnalysis.diagnostics.profile;
    const first = profile[0];
    const last = profile.at(-1);
    if (!first || !last || profile.length < 2) return null;
    const diagnostics = analyzeLineOfSight({
      frequencyGHz: linkAnalysisRequest.frequencyGHz,
      startAntennaElevationM: first.terrainElevationM + linkAnalysisRequest.startAntennaHeightM,
      endAntennaElevationM: last.terrainElevationM + linkAnalysisRequest.endAntennaHeightM,
      samples: profile.map(({ distanceM, terrainElevationM, effectiveSurfaceM, source, obstacleName }) => ({
        distanceM, terrainElevationM, effectiveSurfaceM, source, ...(obstacleName ? { obstacleName } : {}),
      })),
      kFactor: linkAnalysisRequest.kFactor,
      requiredClearancePercent: linkAnalysis.diagnostics.requiredClearancePercent,
    });
    return { ...linkAnalysis, diagnostics, heightRecommendations: [] };
  }, [linkAnalysis, linkAnalysisRequest, terrainPathKey]);
  const activeLinkAnalysis = settledLinkAnalysis ?? previewLinkAnalysis;
  const linkObstructed = selectedRadioLink?.kind === 'ptp'
    && (activeLinkAnalysis?.diagnostics.quality === 'blocked' || activeLinkAnalysis?.diagnostics.visible === false);
  const directionalBudgets = useMemo(() => {
    if (!linkStartSite || !linkEndSite) return null;
    const distanceKm = haversineDistanceM(linkStartSite.coordinates, linkEndSite.coordinates) / 1_000;
    return {
      forward: directionalLinkBudget(linkStartSite, linkEndSite, distanceKm, rainRateMmPerHour, selectedRadioLink?.additionalLossDb ?? 0),
      reverse: directionalLinkBudget(linkEndSite, linkStartSite, distanceKm, rainRateMmPerHour, selectedRadioLink?.additionalLossDb ?? 0),
    };
  }, [linkStartSite, linkEndSite, rainRateMmPerHour, selectedRadioLink?.additionalLossDb]);
  const ptpCapacity = useMemo(() => selectedRadioLink?.kind === 'ptp' && directionalBudgets && linkStartSite && linkEndSite
    ? calculatePtPCapacity(directionalBudgets, [linkStartSite, linkEndSite])
    : null, [selectedRadioLink?.kind, directionalBudgets, linkStartSite, linkEndSite]);
  const autoEquipmentKey = useMemo(() => {
    if (!linkStartSite || !linkEndSite || !selectedRadioLink || selectedRadioLink.productSelectionMode !== 'auto') return null;
    return JSON.stringify({
      projectId: currentProjectId,
      linkId: selectedRadioLink.id,
      kind: selectedRadioLink.kind,
      startSiteId: linkStartSite.id,
      endSiteId: linkEndSite.id,
      targetSiteIds: automaticEquipmentTargetIds,
      startCoordinates: linkStartSite.coordinates,
      endCoordinates: linkEndSite.coordinates,
      channelWidthMHz: linkStartSite.channelWidthMHz,
      cableLossDb: linkStartSite.cableLossDb,
      additionalLossDb: selectedRadioLink.additionalLossDb,
      ambientNoiseFloorDbm: linkEndSite.ambientNoiseFloorDbm,
    });
  }, [automaticEquipmentTargetIds, currentProjectId, linkStartSite, linkEndSite, selectedRadioLink]);
  const activeElevationProfile = terrainPathKey && displayedTerrainKeyRef.current === terrainPathKey ? elevationProfile : null;
  const sectorSite = selectedSite?.type === 'base_station' ? selectedSite : activePtMpGroup ? sites.find((site) => site.id === activePtMpGroup.apSiteId) ?? null : null;
  const dynamicSectorRadiusM = activePtMpGroup ? Math.max(250, ...activePtMpGroup.stationSiteIds.map((id) => { const station = sites.find((site) => site.id === id); return sectorSite && station ? haversineDistanceM(sectorSite.coordinates, station.coordinates) * 1.2 : 0; })) : coverageConfig.maxCoverageRadiusM;
  const coverageBoundary = useMemo(() => sectorSite ? createCoverageBoundary(sectorSite.coordinates, dynamicSectorRadiusM, coverageConfig.azimuthDeg, coverageConfig.horizontalBeamwidthDeg) : [], [sectorSite, dynamicSectorRadiusM, coverageConfig]);
  const clientStatuses = useMemo(() => Object.fromEntries((clientAnalysis?.clients ?? []).map((client) => [client.clientId, client.availability])), [clientAnalysis]);
  const selectedClientAnalysis = selectedSite ? clientAnalysis?.clients.find((client) => client.clientId === selectedSite.id) ?? null : null;
  const interferenceSectors = useMemo<InterferenceSector[]>(() => sites.filter((site) => site.type === 'base_station').map((site) => ({ id: site.id, name: site.name, lat: site.coordinates.lat, lon: site.coordinates.lon, frequencyMHz: site.frequencyGHz * 1_000, channelWidthMHz: site.channelWidthMHz, txPowerDbm: site.txPowerDbm, cableLossDb: site.cableLossDb, antennaGainDbi: site.antennaGainDbi, azimuthDeg: coverageConfig.azimuthDeg, beamwidthDeg: coverageConfig.horizontalBeamwidthDeg, frontToBackDb: 28, maxRadiusM: coverageConfig.maxCoverageRadiusM, ambientNoiseFloorDbm: site.ambientNoiseFloorDbm })), [sites, coverageConfig]);
  const interferenceAnalysis = useMemo(() => calculatePlannedInterference(interferenceSectors), [interferenceSectors]);
  const workspaceObjects = useMemo(() => serializeProjectWorkspace(sites, obstacles, radioLinks, ptmpGroups, displaySettings), [sites, obstacles, radioLinks, ptmpGroups, displaySettings]);
  const projectSaveSnapshot = useMemo(() => ({
    objects: workspaceObjects,
    viewState: {
      ...mapViewState,
      activeLayers: ['detected_obstacles', ...(coverageOverlay ? ['coverage'] : []), ...(interferenceOverlay ? ['interference'] : [])],
      selectedObjectId: selectedSiteId,
    } as ProjectViewState,
    displaySettingsJson: JSON.stringify(displaySettings),
    coverageConfig,
    rainRateMmPerHour,
  }), [workspaceObjects, mapViewState, selectedSiteId, coverageOverlay, interferenceOverlay, displaySettings, coverageConfig, rainRateMmPerHour]);
  const projectSaveSignature = useMemo(() => JSON.stringify(projectSaveSnapshot), [projectSaveSnapshot]);
  const latestProjectSaveRef = useRef({ snapshot: projectSaveSnapshot, signature: projectSaveSignature });
  latestProjectSaveRef.current = { snapshot: projectSaveSnapshot, signature: projectSaveSignature };
  const currentProjectIdRef = useRef(currentProjectId);
  currentProjectIdRef.current = currentProjectId;
  const draftPayload = useMemo<WorkspaceDraftPayload>(() => ({
    projectId: currentProjectId,
    projectName,
    objects: workspaceObjects,
    viewState: { ...mapViewState, selectedObjectId: selectedSiteId },
    displaySettings,
    coverageConfig,
    selectedRadioLinkId,
    rainRateMmPerHour,
    dirty: saveState !== 'saved',
  }), [currentProjectId, projectName, workspaceObjects, displaySettings, mapViewState, selectedSiteId, coverageConfig, selectedRadioLinkId, rainRateMmPerHour, saveState]);
  const latestDraftPayloadRef = useRef(draftPayload);
  latestDraftPayloadRef.current = draftPayload;
  const initialLocationRequestStartedRef = useRef(false);
  const initialLocationEligibleRef = useRef(false);
  initialLocationEligibleRef.current = currentProjectId === null
    && workspaceObjects.length === 0
    && mapResetRevision === 0
    && initialViewIsUntouched(mapViewState);

  useEffect(() => {
    api.me()
      .then(({ user }) => setCurrentUser(user))
      .catch(() => undefined)
      .finally(() => setSessionResolved(true));
  }, []);

  useEffect(() => {
    if (!sessionResolved || (currentUser && draftReadyUserId !== currentUser.userId) || initialLocationRequestStartedRef.current) return;
    initialLocationRequestStartedRef.current = true;
    if (!initialLocationEligibleRef.current || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      if (!initialLocationEligibleRef.current) return;
      const lat = position.coords.latitude;
      const lon = position.coords.longitude;
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) return;
      setMapViewState((current) => ({
        ...current,
        center: { lat, lon },
        zoom: geolocationZoom(position.coords.accuracy),
      }));
      setMapResetRevision((revision) => revision + 1);
    }, () => undefined, { enableHighAccuracy: false, timeout: 12_000, maximumAge: 900_000 });
  }, [sessionResolved, currentUser, draftReadyUserId]);

  useEffect(() => {
    let cancelled = false;
    api.equipmentCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setEquipmentRadios(catalog.radios);
        setEquipmentAntennas(catalog.antennas);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sites.some((site) => clampSiteTxPowerDbm(site, site.txPowerDbm, equipmentRadios) !== site.txPowerDbm)) return;
    setSites((current) => current.map((site) => {
      const txPowerDbm = clampSiteTxPowerDbm(site, site.txPowerDbm, equipmentRadios);
      return txPowerDbm === site.txPowerDbm ? site : { ...site, txPowerDbm };
    }));
    setSaveState('dirty');
  }, [sites, equipmentRadios]);

  useEffect(() => {
    if (!currentUser) { setDraftReadyUserId(null); return; }
    if (draftReadyUserId === currentUser.userId) return;

    const preserveVisibleWorkspace = saveState !== 'saved';
    let draft = null;
    try { draft = loadWorkspaceDraft(window.localStorage, currentUser.userId); }
    catch { draft = null; }
    if (preserveVisibleWorkspace && draft) {
      const keepVisibleWorkspace = window.confirm('В аккаунте уже есть временный черновик. Нажмите OK, чтобы заменить его текущей схемой, или «Отмена», чтобы восстановить сохранённый черновик аккаунта.');
      if (keepVisibleWorkspace) draft = null;
    }

    if (draft) {
      try {
        const workspace = hydrateProjectWorkspace(draftProjectRecords(draft), { inferLegacyLink: false });
        const nextViewState = hydrateProjectViewState({ viewState: draft.viewState }, null, initialMapViewState);
        const selectedLink = workspace.radioLinks.find((link) => link.id === draft.selectedRadioLinkId) ?? workspace.radioLinks[0] ?? null;
        setProjectName(draft.projectName || 'Новый проект');
        setCurrentProjectId(draft.projectId);
        setMapViewState(nextViewState);
        setMapResetRevision((revision) => revision + 1);
        setSites(workspace.sites);
        setObstacles(workspace.obstacles);
        setLinkEndpointIds(selectedLink ? [selectedLink.startSiteId, selectedLink.endSiteId] : workspace.linkEndpointIds ?? ['', '']);
        setRadioLinks(workspace.radioLinks);
        setPtmpGroups(workspace.ptmpGroups);
        setSelectedRadioLinkId(selectedLink?.id ?? null);
        setDisplaySettings(workspace.displaySettings);
        persistedDisplaySettingsRef.current = JSON.stringify(workspace.displaySettings);
        setCoverageConfig(hydrateDraftCoverageConfig(draft.coverageConfig));
        setRainRateMmPerHour(Math.max(0, Math.min(100, draft.rainRateMmPerHour)));
        setSelectedSiteId(workspace.sites.some((site) => site.id === nextViewState.selectedObjectId) ? nextViewState.selectedObjectId : null);
        setSelectedSiteAnchor(null);
        setActiveTool('select');
        setRadioDraftStartSiteId(null);
        setRadioDraftStaged(false);
        setLinkAnalysis(null);
        setResolvedLinkAnalysisKey(null);
        setElevationProfile(null);
        elevationProfileRef.current = null;
        displayedTerrainKeyRef.current = null;
        setTerrainCacheSource(null);
        setDetectedObstacles([]);
        setCoverageJob(null);
        setCoverageOverlay(null);
        setClientAnalysis(null);
        undoStackRef.current = [];
        redoStackRef.current = [];
        setHistoryRevision((revision) => revision + 1);
        setSaveState(draft.dirty ? 'dirty' : 'saved');
      } catch {
        setObstacleSaveError('Временный черновик повреждён и не был восстановлен');
      }
    }
    setDraftReadyUserId(currentUser.userId);
  }, [currentUser, draftReadyUserId]);

  useEffect(() => {
    if (!currentUser || draftReadyUserId !== currentUser.userId) return;
    const timer = window.setTimeout(() => {
      let saved = false;
      try { saved = saveWorkspaceDraft(window.localStorage, currentUser.userId, draftPayload); }
      catch { saved = false; }
      if (!saved) setObstacleSaveError('Не удалось сохранить временный черновик в браузере');
    }, 150);
    return () => window.clearTimeout(timer);
  }, [currentUser, draftReadyUserId, draftPayload]);

  useEffect(() => {
    if (saveState === 'saved') return;
    const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (currentUser && draftReadyUserId === currentUser.userId) {
        try { saveWorkspaceDraft(window.localStorage, currentUser.userId, draftPayload); } catch { /* The warning still protects an unavailable local draft. */ }
      }
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [saveState, currentUser, draftReadyUserId, draftPayload]);

  useEffect(() => {
    const closeCurrentOverlay = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (activeTool !== 'select') { setActiveTool(cancelMapTool()); setRadioDraftStartSiteId(null); setRadioDraftStaged(false); event.preventDefault(); return; }
      if (contextPoint) { setContextPoint(null); return; }
      if (mapTypeOpen) { setMapTypeOpen(false); return; }
      if (displayDrawerOpen) { setDisplayDrawerOpen(false); return; }
      if (selectedSiteId) { setSelectedSiteId(null); setSelectedSiteAnchor(null); return; }
      if (selectedRadioLinkId) { setSelectedRadioLinkId(null); return; }
    };
    window.addEventListener('keydown', closeCurrentOverlay);
    return () => window.removeEventListener('keydown', closeCurrentOverlay);
  }, [activeTool, contextPoint, mapTypeOpen, displayDrawerOpen, selectedSiteId, selectedRadioLinkId]);

  useEffect(() => {
    if (JSON.stringify(displaySettings) !== persistedDisplaySettingsRef.current) setSaveState('dirty');
  }, [displaySettings]);

  useEffect(() => {
    if (!currentUser) {
      analysisCacheRef.current.clear();
      analysisCacheUserIdRef.current = null;
      setAnalysisBusy(false);
      setLinkAnalysis(null);
      setResolvedLinkAnalysisKey(null);
      setElevationProfile(null);
      elevationProfileRef.current = null;
      displayedTerrainKeyRef.current = null;
      setTerrainCacheSource(null);
      setDetectedObstacles([]);
      setObstacleWarnings([]);
      setElevationError('Войдите, чтобы построить рельеф, LOS и зону Френеля');
      return;
    }
    if (analysisCacheUserIdRef.current !== currentUser.userId) {
      analysisCacheRef.current.clear();
      analysisCacheUserIdRef.current = currentUser.userId;
    }
    if (!linkAnalysisRequest || !linkAnalysisKey || !terrainPathKey) {
      setAnalysisBusy(false);
      setLinkAnalysis(null);
      setResolvedLinkAnalysisKey(null);
      setElevationProfile(null);
      elevationProfileRef.current = null;
      displayedTerrainKeyRef.current = null;
      setTerrainCacheSource(null);
      setDetectedObstacles([]);
      setObstacleWarnings([]);
      setElevationError(null);
      return;
    }
    if (siteMoveActive) {
      setAnalysisBusy(false);
      setElevationError(null);
      return;
    }
    const scopedAnalysisKey = `${currentUser.userId}:${linkAnalysisKey}`;
    const cachedAnalysis = readCachedLinkAnalysis(analysisCacheRef.current, scopedAnalysisKey);
    if (cachedAnalysis) {
      setLinkAnalysis(cachedAnalysis);
      setResolvedLinkAnalysisKey(scopedAnalysisKey);
      setElevationProfile(cachedAnalysis.elevationProfile);
      elevationProfileRef.current = cachedAnalysis.elevationProfile;
      displayedTerrainKeyRef.current = terrainPathKey;
      setTerrainCacheSource('memory');
      setDetectedObstacles(cachedAnalysis.obstacles.filter((obstacle) => obstacle.source !== 'Пользовательское препятствие'));
      setObstacleWarnings(cachedAnalysis.obstacleWarnings);
      setElevationError(null);
      setAnalysisBusy(false);
      void terrainProfileCache.set(terrainPathKey, cachedAnalysis.elevationProfile);
      return;
    }

    const terrainAlreadyDisplayed = displayedTerrainKeyRef.current === terrainPathKey && elevationProfileRef.current !== null;
    if (!terrainAlreadyDisplayed) setLinkAnalysis(null);
    setResolvedLinkAnalysisKey(null);
    setDetectedObstacles([]);
    setObstacleWarnings([]);
    setElevationError(null);
    if (!terrainAlreadyDisplayed) {
      displayedTerrainKeyRef.current = terrainPathKey;
      setElevationProfile(null);
      elevationProfileRef.current = null;
      setTerrainCacheSource(null);
    }
    setAnalysisBusy(true);
    let cancelled = false;
    let analysisCompleted = false;

    if (!terrainAlreadyDisplayed) void terrainProfileCache.get(terrainPathKey).then((profile) => {
      if (cancelled || analysisCompleted) return;
      if (profile) {
        displayedTerrainKeyRef.current = terrainPathKey;
        elevationProfileRef.current = profile;
        setElevationProfile(profile);
        setTerrainCacheSource('storage');
        return;
      }
    }).catch(() => undefined);

    const timer = window.setTimeout(() => {
      const runLatestAnalysis = async (): Promise<void> => {
        if (cancelled) return;
        try {
          let result: Awaited<ReturnType<typeof api.analyzeLink>> | null = null;
          for (let attempt = 0; attempt < 3 && !cancelled; attempt += 1) {
            try {
              result = await api.analyzeLink(linkAnalysisRequest);
              break;
            } catch (reason) {
              if (!(reason instanceof ApiError) || reason.status !== 429 || attempt === 2) throw reason;
              await new Promise<void>((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
            }
          }
          if (cancelled || !result) return;
          analysisCompleted = true;
          if (isReusableLinkAnalysis(result.analysis)) cacheLinkAnalysis(analysisCacheRef.current, scopedAnalysisKey, result.analysis);
          setLinkAnalysis(result.analysis);
          setResolvedLinkAnalysisKey(scopedAnalysisKey);
          const retainedTerrainProfile = displayedTerrainKeyRef.current === terrainPathKey ? elevationProfileRef.current : null;
          if (!retainedTerrainProfile) {
            setElevationProfile(result.analysis.elevationProfile);
            elevationProfileRef.current = result.analysis.elevationProfile;
            displayedTerrainKeyRef.current = terrainPathKey;
            setTerrainCacheSource('memory');
            void terrainProfileCache.set(terrainPathKey, result.analysis.elevationProfile);
          }
          setDetectedObstacles(result.analysis.obstacles.filter((obstacle) => obstacle.source !== 'Пользовательское препятствие'));
          setObstacleWarnings(result.analysis.obstacleWarnings);
          setElevationError(null);
          setAnalysisBusy(false);
        } catch (reason) {
          if (!cancelled) {
            setLinkAnalysis(null);
            setResolvedLinkAnalysisKey(null);
            setElevationError(reason instanceof Error ? reason.message : 'Не удалось рассчитать радиотрассу');
            setAnalysisBusy(false);
          }
        }
      };
      const queued = analysisRequestTailRef.current.catch(() => undefined).then(runLatestAnalysis);
      analysisRequestTailRef.current = queued;
    }, 450);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [linkAnalysisKey, terrainPathKey, currentUser, siteMoveActive]);

  useEffect(() => {
    const start = linkSites[0];
    const end = linkSites[1];
    if (!autoEquipmentKey || !start || !end || !selectedRadioLink || selectedRadioLink.productSelectionMode !== 'auto') { setProductBusy(false); return; }
    const controller = new AbortController();
    const distanceKm = haversineDistanceM(start.coordinates, end.coordinates) / 1_000;
    const timer = window.setTimeout(() => {
      setProductBusy(true);
      const parameters = { automatic: true, distanceKm, channelWidthMHz: start.channelWidthMHz, requiredThroughputMbps: selectedRadioLink.kind === 'ptmp' ? 50 : 100, maxEirpDbm: 36, cableLossDb: start.cableLossDb, additionalLossDb: selectedRadioLink.additionalLossDb, ambientNoiseFloorDbm: end.ambientNoiseFloorDbm };
      const request = selectedRadioLink.kind === 'ptmp'
        ? Promise.all([api.recommendEquipment({ ...parameters, kind: 'ptmp' }), api.recommendEquipment({ ...parameters, kind: 'station' })]).then(([ap, station]) => ({ recommendations: ap.recommendations, peerRecommendation: station.recommendations[0] ?? null }))
        : api.recommendEquipment({ ...parameters, kind: 'ptp' }).then((result) => ({ recommendations: result.recommendations, peerRecommendation: result.recommendations[0] ?? null }));
      request.then(({ recommendations, peerRecommendation }) => {
          if (controller.signal.aborted) return;
          const recommendation = recommendations[0] ?? null;
          const completeRecommendation = recommendation?.channelWidthMHz === start.channelWidthMHz
            && (selectedRadioLink.kind !== 'ptmp' || peerRecommendation?.channelWidthMHz === start.channelWidthMHz);
          setAutoRecommendation(completeRecommendation ? recommendation : null);
          if (recommendation && completeRecommendation) {
            const targetIds = automaticEquipmentTargetIds;
            const targetIdSet = new Set(targetIds);
            const targetSnapshot = sites.filter((site) => targetIdSet.has(site.id));
            const updatedSnapshot = applyAutoEquipmentRecommendations(targetSnapshot, targetIds, start.id, start.channelWidthMHz, recommendation, peerRecommendation);
            if (updatedSnapshot !== targetSnapshot) {
              setSites((current) => applyAutoEquipmentRecommendations(current, targetIds, start.id, start.channelWidthMHz, recommendation, peerRecommendation));
              setSaveState('dirty');
            }
          }
        })
        .catch(() => { if (!controller.signal.aborted) setAutoRecommendation(null); })
        .finally(() => { if (!controller.signal.aborted) setProductBusy(false); });
    }, 650);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [autoEquipmentKey]);

  useEffect(() => {
    if (!coverageJob || coverageJob.status === 'completed' || coverageJob.status === 'failed' || coverageJob.status === 'cancelled') return;
    const timer = window.setTimeout(() => {
      api.coverageJob(coverageJob.id).then(({ job }) => setCoverageJob(job)).catch((reason: unknown) => setCoverageError(reason instanceof Error ? reason.message : 'Не удалось получить статус покрытия'));
    }, 700);
    return () => window.clearTimeout(timer);
  }, [coverageJob]);

  useEffect(() => {
    if (coverageJob?.status !== 'completed' || !coverageJob.result) return;
    try { setCoverageOverlay(renderCoverageOverlay(coverageJob.result)); setCoverageError(null); }
    catch (reason) { setCoverageError(reason instanceof Error ? reason.message : 'Не удалось отрисовать покрытие'); }
  }, [coverageJob]);

  const linkMetrics = useMemo(() => {
    const start = linkSites[0];
    const end = linkSites[1];
    if (!start || !end) return { distanceKm: 0, bearing: 0, rxDbm: 0, throughput: 0, fadeMarginDb: 0, modulation: '—' };
    const distanceKm = haversineDistanceM(start.coordinates, end.coordinates) / 1_000;
    const forwardBudget = directionalBudgets?.forward;
    const throughput = ptpCapacity?.totalMbps
      ?? (directionalBudgets ? Math.min(directionalBudgets.forward.usableThroughputMbps, directionalBudgets.reverse.usableThroughputMbps) : 0);
    return {
      distanceKm: activeLinkAnalysis?.distanceM ? activeLinkAnalysis.distanceM / 1_000 : distanceKm,
      bearing: activeLinkAnalysis?.bearingAToB ?? initialBearingDeg(start.coordinates, end.coordinates),
      rxDbm: forwardBudget?.rxSignalDbm ?? 0,
      throughput: Math.round(throughput),
      fadeMarginDb: forwardBudget?.fadeMarginDb ?? 0,
      modulation: forwardBudget?.modulation ?? '—',
    };
  }, [linkSites, activeLinkAnalysis, directionalBudgets, ptpCapacity]);

  const sitePopupLinks = useMemo<SitePopupLink[]>(() => {
    if (!selectedSite) return [];
    const analyzedLink = radioLinks.find((link) => link.startSiteId === linkEndpointIds[0] && link.endSiteId === linkEndpointIds[1]);
    return radioLinks.flatMap((link) => {
      if (link.startSiteId !== selectedSite.id && link.endSiteId !== selectedSite.id) return [];
      const peerId = link.startSiteId === selectedSite.id ? link.endSiteId : link.startSiteId;
      const peer = sites.find((site) => site.id === peerId);
      if (!peer) return [];
      const distanceM = haversineDistanceM(selectedSite.coordinates, peer.coordinates);
      const capacityLabel = analyzedLink?.id === link.id && directionalBudgets && !analysisBusy && !linkObstructed ? `${linkMetrics.throughput} Мбит/с` : '—';
      return [{ link, peer, distanceLabel: formatDistance(distanceM, displaySettings.units), capacityLabel }];
    });
  }, [selectedSite, radioLinks, linkEndpointIds, sites, directionalBudgets, linkMetrics.throughput, analysisBusy, linkObstructed, displaySettings.units]);

  const nearbySiteCount = useMemo(() => selectedSite ? sites.filter((site) => site.id !== selectedSite.id && haversineDistanceM(site.coordinates, selectedSite.coordinates) <= 5_000).length : 0, [selectedSite, sites]);

  const ptmpStationRows = useMemo(() => {
    if (!activePtMpGroup) return [];
    const ap = sites.find((site) => site.id === activePtMpGroup.apSiteId);
    if (!ap) return [];
    return activePtMpGroup.stationSiteIds.flatMap((id, index) => {
      const station = sites.find((site) => site.id === id);
      if (!station) return [];
      const distanceM = haversineDistanceM(ap.coordinates, station.coordinates);
      const isSelected = id === selectedRadioLink?.endSiteId;
      const capacityMbps = isSelected && directionalBudgets ? Math.min(directionalBudgets.forward.usableThroughputMbps, directionalBudgets.reverse.usableThroughputMbps) : Math.max(30, 1_070 * (1 - Math.min(0.82, distanceM / 80_000)));
      return [{ id, number: index + 1, site: station, distanceM, capacityMbps }];
    });
  }, [activePtMpGroup, sites, selectedRadioLink, directionalBudgets]);
  const averagePtMpCapacity = ptmpStationRows.length > 0 ? ptmpStationRows.reduce((sum, row) => sum + row.capacityMbps, 0) / ptmpStationRows.length : 0;

  const qualityLabel = activeLinkAnalysis ? ({ excellent: 'Отличный линк', good: 'Нормальный линк', marginal: 'Пограничный линк', blocked: 'Нет прямой видимости' } as const)[activeLinkAnalysis.diagnostics.quality] : 'Ожидание расчёта';
  const heightRecommendation = activeLinkAnalysis?.heightRecommendations.find((item) => item.mode === optimizationMode && item.targetClearancePercent === optimizationTarget) ?? null;

  const captureWorkspace = (): WorkspaceSnapshot => ({
    sites: sites.map((site) => ({ ...site, coordinates: { ...site.coordinates } })),
    obstacles: obstacles.map((obstacle) => ({ ...obstacle, coordinates: { ...obstacle.coordinates } })),
    linkEndpointIds: [...linkEndpointIds],
    coverageConfig: { ...coverageConfig },
    selectedSiteId,
    radioLinks: radioLinks.map((link) => ({ ...link })),
    ptmpGroups: ptmpGroups.map((group) => ({ ...group, stationSiteIds: [...group.stationSiteIds] })),
    selectedRadioLinkId,
    displaySettings: { ...displaySettings },
  });

  const restoreWorkspace = (snapshot: WorkspaceSnapshot): void => {
    setSites(snapshot.sites);
    setObstacles(snapshot.obstacles);
    setLinkEndpointIds(snapshot.linkEndpointIds);
    setCoverageConfig(snapshot.coverageConfig);
    setSelectedSiteId(snapshot.selectedSiteId);
    setRadioLinks(snapshot.radioLinks);
    setPtmpGroups(snapshot.ptmpGroups);
    setSelectedRadioLinkId(snapshot.selectedRadioLinkId);
    setDisplaySettings(snapshot.displaySettings);
    setCoverageJob(null);
    setCoverageOverlay(null);
    setClientAnalysis(null);
    setSaveState('dirty');
  };

  const recordHistory = (): void => {
    undoStackRef.current.push(captureWorkspace());
    if (undoStackRef.current.length > 100) undoStackRef.current.shift();
    redoStackRef.current = [];
    setHistoryRevision((revision) => revision + 1);
  };

  const undo = (): void => {
    const snapshot = undoStackRef.current.pop();
    if (!snapshot) return;
    redoStackRef.current.push(captureWorkspace());
    restoreWorkspace(snapshot);
    setHistoryRevision((revision) => revision + 1);
  };

  const redo = (): void => {
    const snapshot = redoStackRef.current.pop();
    if (!snapshot) return;
    undoStackRef.current.push(captureWorkspace());
    restoreWorkspace(snapshot);
    setHistoryRevision((revision) => revision + 1);
  };

  const createSite = (coordinates: Coordinates, type: SiteType): void => {
    recordHistory();
    const id = `site-${createClientId().replace(/[^a-z0-9]/giu, '').slice(-10)}`;
    const site: DesignSite = {
      id,
      name: allocateStationNames(sites.map((item) => item.name))[0]!,
      type,
      coordinates,
      groundElevationM: null,
      antennaHeightM: type === 'base_station' ? 24 : 12,
      equipmentName: type === 'base_station' ? 'Freedom Sector 5' : 'Freedom Beam 5',
      frequencyGHz: 5.8,
      channelWidthMHz: 40,
      txPowerDbm: 18,
      antennaGainDbi: type === 'base_station' ? 17 : 23,
      cableLossDb: 1,
      noiseFigureDb: 6,
      ambientNoiseFloorDbm: -95,
    };
    setSites((current) => [...current, site]);
    setSelectedSiteId(id);
    setActiveTool('select');
    setContextPoint(null);
    setSaveState('dirty');
  };

  const createRadio = (kind: RadioLinkKind, endpoints: MapRadioEndpoint[]): void => {
    if ((kind === 'ptp' && endpoints.length < 2) || (kind === 'ptmp' && endpoints.length < 3)) return;
    recordHistory();
    const token = createClientId().replace(/[^a-z0-9]/giu, '').slice(-8);
    const selectionMode = displaySettings.defaultProductSelection;
    if (kind === 'ptp') {
      const startEndpoint = endpoints[0] as MapRadioEndpoint;
      const endEndpoint = endpoints[1] as MapRadioEndpoint;
      const startCoordinates = startEndpoint.coordinates;
      const endCoordinates = endEndpoint.coordinates;
      const distanceM = haversineDistanceM(startCoordinates, endCoordinates);
      const shortRange = distanceM <= 12_000;
      const startId = startEndpoint.siteId ?? `ptp-${token}-a`;
      const endId = endEndpoint.siteId ?? `ptp-${token}-b`;
      const linkId = `ptp-${token}`;
      const frequencyGHz = shortRange ? 60.48 : 5.8;
      const channelWidthMHz = shortRange ? 2160 : 50;
      const equipmentName = shortRange ? 'airFiber 60 Long-Range' : 'airFiber 5XHD';
      const gain = shortRange ? 43 : 30;
      const existingStartSite = sites.find((site) => site.id === startId) ?? null;
      const existingEndSite = sites.find((site) => site.id === endId) ?? null;
      const allocatedNames = allocateStationNames(sites.map((site) => site.name), Number(existingStartSite === null) + Number(existingEndSite === null));
      let allocatedNameIndex = 0;
      const startSite: DesignSite = existingStartSite ?? { id: startId, name: allocatedNames[allocatedNameIndex++]!, type: 'base_station', coordinates: startCoordinates, groundElevationM: null, antennaHeightM: 18, equipmentName, frequencyGHz, channelWidthMHz, txPowerDbm: shortRange ? 21 : 25, antennaGainDbi: gain, cableLossDb: shortRange ? 0 : 1, noiseFigureDb: 6, ambientNoiseFloorDbm: -95 };
      const endSite: DesignSite = existingEndSite ?? { id: endId, name: allocatedNames[allocatedNameIndex++]!, type: 'client', coordinates: endCoordinates, groundElevationM: null, antennaHeightM: displaySettings.defaultStationHeightM, equipmentName, frequencyGHz, channelWidthMHz, txPowerDbm: shortRange ? 21 : 25, antennaGainDbi: gain, cableLossDb: shortRange ? 0 : 1, noiseFigureDb: 6, ambientNoiseFloorDbm: -95 };
      const createdSites = [existingStartSite ? null : startSite, existingEndSite ? null : endSite].filter((site): site is DesignSite => site !== null);
      const link: RadioLinkModel = { id: linkId, name: `${startSite.name} → ${endSite.name}`, kind: 'ptp', startSiteId: startId, endSiteId: endId, groupId: null, stationNumber: null, productSelectionMode: selectionMode, additionalLossDb: 0 };
      setSites((current) => [...current, ...createdSites]);
      setRadioLinks((current) => [...current, link]);
      setLinkEndpointIds([startId, endId]);
      setSelectedRadioLinkId(linkId);
      setSelectedSiteId(null);
    } else {
      const apCoordinates = (endpoints[0] as MapRadioEndpoint).coordinates;
      const stationCoordinates = endpoints.slice(1, 3).map((endpoint) => endpoint.coordinates);
      const groupId = `ptmp-${token}`;
      const apId = `${groupId}-ap`;
      const stationIds = stationCoordinates.map((_, index) => `${groupId}-s${index + 1}`);
      const allocatedNames = allocateStationNames(sites.map((site) => site.name), 1 + stationCoordinates.length);
      const ap: DesignSite = { id: apId, name: allocatedNames[0]!, type: 'base_station', coordinates: apCoordinates, groundElevationM: null, antennaHeightM: 24, equipmentName: 'LTU Rocket', frequencyGHz: 5.8, channelWidthMHz: 40, txPowerDbm: 22, antennaGainDbi: 17, cableLossDb: 1, noiseFigureDb: 6, ambientNoiseFloorDbm: -95 };
      const stations: DesignSite[] = stationCoordinates.map((point, index) => ({ id: stationIds[index]!, name: allocatedNames[index + 1]!, type: 'client', coordinates: point, groundElevationM: null, antennaHeightM: displaySettings.defaultStationHeightM, equipmentName: index === 0 ? 'LTU Extreme-Range' : 'LTU Long-Range', frequencyGHz: 5.8, channelWidthMHz: 40, txPowerDbm: 22, antennaGainDbi: index === 0 ? 29 : 26, cableLossDb: 0, noiseFigureDb: 6, ambientNoiseFloorDbm: -95 }));
      const group: PtMpGroupModel = { id: groupId, name: ap.name, apSiteId: apId, stationSiteIds: stationIds, selectedStationId: stationIds[0] ?? null, productSelectionMode: selectionMode };
      const links: RadioLinkModel[] = stations.map((station, index) => ({ id: `${groupId}:station:${index + 1}`, name: `${ap.name} → ${station.name}`, kind: 'ptmp', startSiteId: apId, endSiteId: station.id, groupId, stationNumber: index + 1, productSelectionMode: selectionMode, additionalLossDb: 0 }));
      const selectedLink = links[0] as RadioLinkModel;
      setSites((current) => [...current, ap, ...stations]);
      setPtmpGroups((current) => [...current, group]);
      setRadioLinks((current) => [...current, ...links]);
      setLinkEndpointIds([apId, stationIds[0]!]);
      setSelectedRadioLinkId(selectedLink.id);
      setSelectedSiteId(null);
      setCoverageConfig((current) => ({ ...current, maxCoverageRadiusM: Math.max(500, ...stations.map((station) => haversineDistanceM(ap.coordinates, station.coordinates) * 1.2)), azimuthDeg: initialBearingDeg(ap.coordinates, stations[0]!.coordinates), horizontalBeamwidthDeg: 90 }));
    }
    setProfileOpen(true);
    setActiveTool('select');
    setRadioDraftStartSiteId(null);
    setRadioDraftStaged(false);
    setContextPoint(null);
    setSaveState('dirty');
  };

  const createRadioAtContext = (kind: RadioLinkKind): void => {
    if (!contextPoint) return;
    const metresPerPixel = 156_543.03392 * Math.cos(contextPoint.lat * Math.PI / 180) / 2 ** mapViewState.zoom;
    const distanceM = Math.max(30, metresPerPixel * 118);
    if (kind === 'ptp') createRadio('ptp', [contextPoint, destinationPoint(contextPoint, 90, distanceM)].map((coordinates) => ({ siteId: null, coordinates })));
    else createRadio('ptmp', [contextPoint, destinationPoint(contextPoint, 68, distanceM), destinationPoint(contextPoint, 112, distanceM)].map((coordinates) => ({ siteId: null, coordinates })));
  };

  const selectRadioLink = (linkId: string): void => {
    const link = radioLinks.find((item) => item.id === linkId);
    if (!link) return;
    setSelectedRadioLinkId(link.id);
    setLinkEndpointIds([link.startSiteId, link.endSiteId]);
    setSelectedSiteId(null);
    setSelectedSiteAnchor(null);
    setProfileOpen(true);
    if (link.groupId) {
      const changesPersistedSelection = ptmpGroups.some((group) => group.id === link.groupId && group.selectedStationId !== link.endSiteId);
      setPtmpGroups((current) => current.map((group) => group.id === link.groupId ? { ...group, selectedStationId: link.endSiteId } : group));
      if (changesPersistedSelection) setSaveState('dirty');
    }
  };

  const addStation = (): void => {
    if (!activePtMpGroup) return;
    const ap = sites.find((site) => site.id === activePtMpGroup.apSiteId);
    if (!ap) return;
    recordHistory();
    const lastStation = sites.find((site) => site.id === activePtMpGroup.stationSiteIds.at(-1));
    const distanceM = Math.max(250, averageGroupDistanceM(activePtMpGroup, sites));
    const bearing = lastStation ? initialBearingDeg(ap.coordinates, lastStation.coordinates) + 24 : coverageConfig.azimuthDeg;
    const identity = allocatePtMpStationIdentity(activePtMpGroup.id, sites.map((site) => site.id), radioLinks.map((link) => link.id));
    const { stationNumber, siteId: stationId, linkId } = identity;
    const station: DesignSite = { id: stationId, name: allocateStationNames(sites.map((site) => site.name))[0]!, type: 'client', coordinates: destinationPoint(ap.coordinates, bearing, distanceM), groundElevationM: null, antennaHeightM: displaySettings.defaultStationHeightM, equipmentName: 'LTU Long-Range', frequencyGHz: ap.frequencyGHz, channelWidthMHz: ap.channelWidthMHz, txPowerDbm: 22, antennaGainDbi: 26, cableLossDb: 0, noiseFigureDb: 6, ambientNoiseFloorDbm: -95 };
    const link: RadioLinkModel = { id: linkId, name: `${ap.name} → ${station.name}`, kind: 'ptmp', startSiteId: ap.id, endSiteId: stationId, groupId: activePtMpGroup.id, stationNumber, productSelectionMode: activePtMpGroup.productSelectionMode, additionalLossDb: 0 };
    setSites((current) => [...current, station]);
    setPtmpGroups((current) => current.map((group) => group.id === activePtMpGroup.id ? { ...group, stationSiteIds: [...group.stationSiteIds, stationId], selectedStationId: stationId } : group));
    setRadioLinks((current) => [...current, link]);
    setSelectedRadioLinkId(link.id);
    setLinkEndpointIds([ap.id, stationId]);
    setSaveState('dirty');
  };

  const changeProfileEndpoint = (endpoint: 'a' | 'b', change: { distanceM?: number; antennaHeightM?: number }): void => {
    const start = linkSites[0];
    const end = linkSites[1];
    if (!start || !end) return;
    const moving = endpoint === 'a' ? start : end;
    const fixed = endpoint === 'a' ? end : start;
    setSites((current) => current.map((site) => {
      if (site.id !== moving.id) return site;
      if (change.antennaHeightM !== undefined) return { ...site, antennaHeightM: Math.round(change.antennaHeightM * 100) / 100 };
      if (change.distanceM !== undefined) return { ...site, coordinates: moveEndpointAlongBearing(fixed, moving, change.distanceM) };
      return site;
    }));
    setSaveState('dirty');
  };

  const updateSelectedProductMode = (mode: 'auto' | 'manual'): void => {
    if (!selectedRadioLink) return;
    setRadioLinks((current) => current.map((link) => link.id === selectedRadioLink.id || (selectedRadioLink.groupId && link.groupId === selectedRadioLink.groupId) ? { ...link, productSelectionMode: mode } : link));
    if (selectedRadioLink.groupId) setPtmpGroups((current) => current.map((group) => group.id === selectedRadioLink.groupId ? { ...group, productSelectionMode: mode } : group));
  };

  const setProductSelectionMode = (mode: 'auto' | 'manual'): void => {
    if (!selectedRadioLink) return;
    recordHistory();
    updateSelectedProductMode(mode);
    if (mode === 'manual') { setEquipmentDialogTargetSiteId(selectedRadioLink.startSiteId); setEquipmentDialogInitialTab('radio'); setEquipmentDialogOpen(true); }
    setSaveState('dirty');
  };

  const patchSite = (siteId: string, patch: Partial<DesignSite>): void => {
    recordHistory();
    setSites((current) => {
      const patched = current.map((site) => {
        if (site.id !== siteId) return site;
        const next = { ...site, ...patch, cableLossDb: patch.cableLossDb === undefined ? site.cableLossDb : normalizeExplicitLinkLossDb(patch.cableLossDb, site.cableLossDb) };
        return { ...next, txPowerDbm: clampSiteTxPowerDbm(next, next.txPowerDbm, equipmentRadios) };
      });
      return patch.channelWidthMHz === undefined
        ? patched
        : patchLinkChannelWidth(patched, siteId, patch.channelWidthMHz, selectedRadioLink, ptmpGroups);
    });
    setSaveState('dirty');
  };

  const patchSelectedLinkAdditionalLoss = (additionalLossDb: number): void => {
    if (!selectedRadioLink || selectedRadioLink.kind !== 'ptp') return;
    recordHistory();
    setRadioLinks((current) => current.map((link) => link.id === selectedRadioLink.id
      ? { ...link, additionalLossDb: normalizeExplicitLinkLossDb(additionalLossDb, link.additionalLossDb) }
      : link));
    setSaveState('dirty');
  };

  const changeHeight = (delta: number): void => {
    if (!selectedSiteId) return;
    recordHistory();
    setSites((current) => current.map((site) => site.id === selectedSiteId ? { ...site, antennaHeightM: Math.max(1, site.antennaHeightM + delta) } : site));
    setSaveState('dirty');
  };

  const changeFrequency = (frequencyGHz: number): void => {
    if (!selectedSiteId || !Number.isFinite(frequencyGHz) || frequencyGHz < 0.1 || frequencyGHz > 100) return;
    recordHistory();
    setSites((current) => current.map((site) => site.id === selectedSiteId ? { ...site, frequencyGHz } : site));
    setSaveState('dirty');
  };

  const moveSite = (siteId: string, coordinates: Coordinates): void => {
    if (!dragHistoryOpenRef.current) recordHistory();
    setSites((current) => current.map((site) => site.id === siteId ? { ...site, coordinates } : site));
    setSaveState('dirty');
  };

  const applyHeightRecommendation = (): void => {
    if (!heightRecommendation?.reachable) return;
    recordHistory();
    setSites((current) => current.map((site) => site.id === linkEndpointIds[0] ? { ...site, antennaHeightM: heightRecommendation.recommendedStartHeightM } : site.id === linkEndpointIds[1] ? { ...site, antennaHeightM: heightRecommendation.recommendedEndHeightM } : site));
    setSaveState('dirty');
  };

  const applyEquipmentRecommendation = (recommendation: EquipmentRecommendation): void => {
    recordHistory();
    setSites((current) => current.map((site) => equipmentTargetSiteIds.includes(site.id) ? {
      ...site,
      equipmentName: displayEquipmentName(recommendation.deviceModel),
      radioDeviceId: recommendation.deviceId,
      radioMaxTxPowerDbm: recommendation.maxTxPowerDbm,
      radioChannelWidthsMHz: [...recommendation.channelWidthsMHz],
      radioReceiverSensitivityDbm: recommendation.receiverSensitivityDbm,
      radioMaxThroughputMbps: recommendation.maxThroughputMbps,
      radioMaxRangeKm: recommendation.maxRangeKm,
      radioPolarization: recommendation.polarization,
      radioPerformanceProfiles: structuredClone(recommendation.performanceProfiles),
      antennaModelId: recommendation.antennaId,
      antennaName: recommendation.antennaModel,
      antennaGainDbi: recommendation.antennaGainDbi,
      txPowerDbm: recommendation.recommendedTxPowerDbm,
      frequencyGHz: recommendation.frequencyMHz / 1_000,
      channelWidthMHz: recommendation.channelWidthMHz,
    } : site));
    updateSelectedProductMode('manual');
    setEquipmentDialogOpen(false);
    setEquipmentDialogTargetSiteId(null);
    setSaveState('dirty');
  };

  const applyCatalogRadio = (radio: RadioDeviceRecord): void => {
    const frequencyForSite = (site: DesignSite): number => radio.minFrequencyMHz !== null && radio.maxFrequencyMHz !== null
      ? Math.min(radio.maxFrequencyMHz, Math.max(radio.minFrequencyMHz, site.frequencyGHz * 1_000 >= radio.minFrequencyMHz && site.frequencyGHz * 1_000 <= radio.maxFrequencyMHz ? site.frequencyGHz * 1_000 : 5_800))
      : site.frequencyGHz * 1_000;
    const clearedAntennaSites = equipmentTargetSites.filter((site) => site.antennaModelId
      && antennaPatchForRadioChange(site, radio, equipmentAntennas, frequencyForSite(site)).antennaModelId === null);
    recordHistory();
    setEquipmentRadios((current) => [...current.filter((candidate) => candidate.id !== radio.id), radio]);
    setSites((current) => current.map((site) => {
      if (!equipmentTargetSiteIds.includes(site.id)) return site;
      const frequencyMHz = frequencyForSite(site);
      const channelWidthMHz = radio.channelWidthsMHz.includes(site.channelWidthMHz)
        ? site.channelWidthMHz
        : radio.channelWidthsMHz.includes(40) ? 40 : radio.channelWidthsMHz.at(-1) ?? site.channelWidthMHz;
      const next: DesignSite = {
        ...site,
        equipmentName: displayEquipmentName(radio.model),
        radioDeviceId: radio.id,
        radioMaxTxPowerDbm: radio.maxTxPowerDbm,
        radioChannelWidthsMHz: [...radio.channelWidthsMHz],
        radioReceiverSensitivityDbm: radio.receiverSensitivityDbm,
        radioMaxThroughputMbps: radio.maxThroughputMbps,
        radioMaxRangeKm: radio.maxRangeKm ?? null,
        radioPolarization: radio.polarization,
        radioPerformanceProfiles: structuredClone(radio.performanceProfiles),
        ...antennaPatchForRadioChange(site, radio, equipmentAntennas, frequencyMHz),
        frequencyGHz: frequencyMHz / 1_000,
        channelWidthMHz,
      };
      return { ...next, txPowerDbm: clampSiteTxPowerDbm(next, next.txPowerDbm, equipmentRadios) };
    }));
    updateSelectedProductMode('manual');
    setEquipmentDialogOpen(false);
    setEquipmentDialogTargetSiteId(null);
    if (clearedAntennaSites.length > 0) {
      setObstacleSaveError(`Пассивная антенна ${clearedAntennaSites.map((site) => `«${site.name}»`).join(', ')} сброшена: она несовместима с ${displayEquipmentName(radio.model)}. Старое усиление очищено.`);
    }
    setSaveState('dirty');
  };

  const applyCatalogAntenna = (antenna: AntennaModelRecord): void => {
    if (antenna.peakGainDbi === null) {
      setObstacleSaveError(`Антенна «${antenna.model}» не применена: усиление не подтверждено.`);
      return;
    }
    const incompatible = equipmentTargetSites.map((site) => ({ site, reason: siteAntennaCompatibilityReason(site, antenna, equipmentRadios) }))
      .find((candidate) => candidate.reason !== null);
    if (incompatible) {
      setObstacleSaveError(`Антенна «${antenna.model}» не применена к «${incompatible.site.name}»: ${incompatible.reason}.`);
      return;
    }
    const compatibilityWarning = equipmentTargetSites.map((site) => ({ site, warning: siteAntennaCompatibilityWarning(site, antenna, equipmentRadios) }))
      .find((candidate) => candidate.warning !== null);
    recordHistory();
    setEquipmentAntennas((current) => [...current.filter((candidate) => candidate.id !== antenna.id), antenna]);
    setSites((current) => current.map((site) => {
      if (!equipmentTargetSiteIds.includes(site.id)) return site;
      const next = { ...site, antennaModelId: antenna.id, antennaName: antenna.model, antennaGainDbi: antenna.peakGainDbi as number };
      return { ...next, txPowerDbm: clampSiteTxPowerDbm(next, next.txPowerDbm, equipmentRadios) };
    }));
    updateSelectedProductMode('manual');
    setEquipmentDialogOpen(false);
    setEquipmentDialogTargetSiteId(null);
    if (compatibilityWarning) {
      setObstacleSaveError(`Антенна «${antenna.model}» применена к «${compatibilityWarning.site.name}» с предупреждением: ${compatibilityWarning.warning}.`);
    }
    setSaveState('dirty');
  };

  const changeCoverageConfig = (patch: Partial<CoverageConfig>): void => {
    recordHistory();
    if (coverageJob && (coverageJob.status === 'queued' || coverageJob.status === 'sampling' || coverageJob.status === 'running')) void api.cancelCoverage(coverageJob.id).catch(() => undefined);
    setCoverageJob(null);
    setCoverageOverlay(null);
    setCoverageConfig((current) => ({ ...current, ...patch }));
    setSaveState('dirty');
  };

  const calculateCoverage = (): void => {
    if (!selectedSite || selectedSite.type !== 'base_station') return;
    if (!currentUser) { setCoverageError('Войдите, чтобы запустить ресурсоёмкий расчёт покрытия'); setAuthDialogOpen(true); return; }
    if (coverageJob && (coverageJob.status === 'queued' || coverageJob.status === 'sampling' || coverageJob.status === 'running')) void api.cancelCoverage(coverageJob.id).catch(() => undefined);
    setCoverageError(null);
    api.startCoverage({
      origin: selectedSite.coordinates,
      antennaHeightM: selectedSite.antennaHeightM,
      receiverHeightM: 2,
      maxCoverageRadiusM: coverageConfig.maxCoverageRadiusM,
      frequencyGHz: selectedSite.frequencyGHz,
      txPowerDbm: selectedSite.txPowerDbm,
      cableLossDb: selectedSite.cableLossDb,
      additionalLossDb: 2,
      receiverAntennaGainDbi: 15,
      antennaPeakGainDbi: selectedSite.antennaGainDbi,
      horizontalBeamwidthDeg: coverageConfig.horizontalBeamwidthDeg,
      verticalBeamwidthDeg: coverageConfig.verticalBeamwidthDeg,
      frontToBackDb: 28,
      horizontalPattern: [],
      verticalPattern: [],
      azimuthDeg: coverageConfig.azimuthDeg,
      mechanicalTiltDeg: coverageConfig.mechanicalTiltDeg,
      electricalTiltDeg: coverageConfig.electricalTiltDeg,
      rainRateMmPerHour,
      clutterLossDb: 0,
      minimumSignalDbm: -90,
      kFactor: 4 / 3,
    }).then(({ job }) => setCoverageJob(job)).catch((reason: unknown) => setCoverageError(reason instanceof Error ? reason.message : 'Не удалось запустить расчёт покрытия'));
  };

  const selectMapSite = (siteId: string | null): void => {
    if (activeTool === 'ptmp' && siteId) {
      const site = sites.find((item) => item.id === siteId);
      if (site?.type !== 'base_station') { setObstacleSaveError('Для PtMP выберите базовую станцию'); return; }
      setActiveTool('select');
    }
    setSelectedSiteId(siteId);
    setSelectedRadioLinkId(null);
    if (!siteId) setSelectedSiteAnchor(null);
  };

  const analyzeClients = (): void => {
    if (!currentUser) { setClientAnalysisError('Войдите, чтобы проанализировать клиентские трассы'); setAuthDialogOpen(true); return; }
    const clients = sites.filter((site) => site.type === 'client');
    const accessPoints = sites.filter((site) => site.type === 'base_station').map((site) => ({ ...site, maxCoverageRadiusM: coverageConfig.maxCoverageRadiusM, loadPercent: 0, predictedInterferenceDbm: -110 }));
    setClientAnalysisBusy(true); setClientAnalysisError(null);
    api.analyzeClients(clients, accessPoints, currentProjectId).then(({ analysis }) => setClientAnalysis(analysis)).catch((reason: unknown) => setClientAnalysisError(reason instanceof Error ? reason.message : 'Не удалось проанализировать клиентов')).finally(() => setClientAnalysisBusy(false));
  };

  const applyClientHeight = (): void => {
    if (!selectedClientAnalysis?.recommendedHeightM) return;
    recordHistory();
    setSites((current) => current.map((site) => site.id === selectedClientAnalysis.clientId ? { ...site, antennaHeightM: selectedClientAnalysis.recommendedHeightM ?? site.antennaHeightM } : site));
    setClientAnalysis(null);
    setSaveState('dirty');
  };

  const toggleInterferenceLayer = (): void => {
    if (interferenceOverlay) { setInterferenceOverlay(null); return; }
    const reference = interferenceSectors[0];
    if (!reference) return;
    const radiusM = Math.max(...interferenceSectors.map((sector) => sector.maxRadiusM));
    const meanLatitude = interferenceSectors.reduce((sum, sector) => sum + sector.lat, 0) / interferenceSectors.length;
    const latPadding = radiusM / 111_320;
    const lonPadding = radiusM / (111_320 * Math.max(0.05, Math.cos(meanLatitude * Math.PI / 180)));
    const bounds = {
      north: Math.max(...interferenceSectors.map((sector) => sector.lat)) + latPadding,
      south: Math.min(...interferenceSectors.map((sector) => sector.lat)) - latPadding,
      east: Math.max(...interferenceSectors.map((sector) => sector.lon)) + lonPadding,
      west: Math.min(...interferenceSectors.map((sector) => sector.lon)) - lonPadding,
    };
    const rows = 129;
    const cols = 129;
    const matrix = calculateInterferenceMatrix({ sectors: interferenceSectors, targetFrequencyMHz: reference.frequencyMHz, targetChannelWidthMHz: reference.channelWidthMHz, ...bounds, rows, cols });
    setInterferenceOverlay(renderInterferenceOverlay(matrix.values, rows, cols, bounds));
  };

  const createObstacle = (obstacle: ManualObstacle): void => {
    recordHistory();
    setObstacles((current) => [...current, obstacle]);
    setActiveTool('select');
    setSaveState('dirty');
  };

  const deleteSelectedSite = (): void => {
    if (!selectedSiteId) return;
    recordHistory();
    const remaining = sites.filter((site) => site.id !== selectedSiteId);
    const remainingLinks = radioLinks.filter((link) => link.startSiteId !== selectedSiteId && link.endSiteId !== selectedSiteId);
    setSites(remaining);
    setRadioLinks(remainingLinks);
    setPtmpGroups((current) => current.flatMap((group) => {
      if (group.apSiteId === selectedSiteId) return [];
      const stationSiteIds = group.stationSiteIds.filter((id) => id !== selectedSiteId);
      return stationSiteIds.length > 0 ? [{ ...group, stationSiteIds, selectedStationId: stationSiteIds.includes(group.selectedStationId ?? '') ? group.selectedStationId : stationSiteIds[0] ?? null }] : [];
    }));
    if (linkEndpointIds.includes(selectedSiteId)) setLinkEndpointIds(remaining.length >= 2 ? [remaining[0]!.id, remaining[1]!.id] : ['', '']);
    if (selectedRadioLink?.startSiteId === selectedSiteId || selectedRadioLink?.endSiteId === selectedSiteId) setSelectedRadioLinkId(remainingLinks[0]?.id ?? null);
    setSelectedSiteId(null);
    setSaveState('dirty');
  };

  const beginSiteMove = (): void => {
    if (!dragHistoryOpenRef.current) {
      recordHistory();
      dragHistoryOpenRef.current = true;
    }
    setSiteMoveActive(true);
  };

  const endSiteMove = (): void => {
    dragHistoryOpenRef.current = false;
    setSiteMoveActive(false);
  };

  const openProject = async (project: ProjectSummary): Promise<void> => {
    const requestId = ++openProjectRequestRef.current;
    const workspaceSignatureAtStart = latestProjectSaveRef.current.signature;
    setObstacleSaveError(null);
    try {
      const result = await api.loadProjectWorkspace(project.id);
      if (requestId !== openProjectRequestRef.current) return;
      if (latestProjectSaveRef.current.signature !== workspaceSignatureAtStart) {
        setObstacleSaveError('Открытие проекта отменено: пока он загружался, рабочая область изменилась');
        return;
      }
      const workspace = hydrateProjectWorkspace(result.objects, { inferLegacyLink: false });
      const nextViewState = hydrateProjectViewState(result.project.settings, result.project.viewport, initialMapViewState);
      const selectedLinkId = workspace.radioLinks[0]?.id ?? null;
      currentProjectIdRef.current = project.id;
      setProjectName(project.name);
      setCurrentProjectId(project.id);
      setMapViewState(nextViewState);
      setMapResetRevision((revision) => revision + 1);
      setSites(workspace.sites);
      setObstacles(workspace.obstacles);
      setLinkEndpointIds(workspace.linkEndpointIds ?? ['', '']);
      setRadioLinks(workspace.radioLinks);
      setPtmpGroups(workspace.ptmpGroups);
      setSelectedRadioLinkId(selectedLinkId);
      setDisplaySettings(workspace.displaySettings);
      persistedDisplaySettingsRef.current = JSON.stringify(workspace.displaySettings);
      setCoverageConfig(initialCoverageConfig);
      setRainRateMmPerHour(0);
      setSelectedSiteId(workspace.sites.some((site) => site.id === nextViewState.selectedObjectId) ? nextViewState.selectedObjectId : null);
      setCoverageJob(null);
      setCoverageOverlay(null);
      setClientAnalysis(null);
      undoStackRef.current = [];
      redoStackRef.current = [];
      setHistoryRevision((revision) => revision + 1);
      let localDraftSaved = !currentUser;
      if (currentUser) {
        try {
          localDraftSaved = saveWorkspaceDraft(window.localStorage, currentUser.userId, {
            projectId: project.id,
            projectName: project.name,
            objects: serializeProjectWorkspace(workspace.sites, workspace.obstacles, workspace.radioLinks, workspace.ptmpGroups, workspace.displaySettings),
            viewState: nextViewState,
            displaySettings: workspace.displaySettings,
            coverageConfig: initialCoverageConfig,
            selectedRadioLinkId: selectedLinkId,
            rainRateMmPerHour: 0,
            dirty: false,
          });
        } catch { localDraftSaved = false; }
      }
      setSaveState(localDraftSaved ? 'saved' : 'error');
      if (!localDraftSaved) setObstacleSaveError('Проект открыт, но временный черновик не удалось сохранить в браузере');
    } catch (reason) {
      if (requestId === openProjectRequestRef.current) setObstacleSaveError(reason instanceof Error ? reason.message : 'Не удалось открыть проект');
    }
  };

  const saveProject = async (targetProjectId: string | null = currentProjectId, targetProjectName = projectName): Promise<boolean> => {
    if (!currentUser) { setAuthDialogOpen(true); setObstacleSaveError('Войдите, чтобы сохранить проект'); return false; }
    if (!targetProjectId) { setProjectDialogOpen(true); setObstacleSaveError('Выберите или создайте проект для сохранения'); return false; }
    if (saveInFlightRef.current) { setObstacleSaveError('Сохранение уже выполняется'); return false; }
    const saving = latestProjectSaveRef.current;
    saveInFlightRef.current = true;
    setSaveInFlight(true);
    setSaveState('saving');
    setObstacleSaveError(null);
    try {
      await api.saveProjectWorkspace(targetProjectId, saving.snapshot.objects, saving.snapshot.viewState);
      const stillCurrentProject = currentProjectIdRef.current === targetProjectId;
      const fullySaved = stillCurrentProject && latestProjectSaveRef.current.signature === saving.signature;
      if (fullySaved) {
        let localDraftSaved = false;
        try {
          localDraftSaved = saveWorkspaceDraft(window.localStorage, currentUser.userId, {
            ...latestDraftPayloadRef.current,
            projectId: targetProjectId,
            projectName: targetProjectName,
            objects: saving.snapshot.objects,
            viewState: saving.snapshot.viewState,
            dirty: false,
          });
        } catch { localDraftSaved = false; }
        if (localDraftSaved) {
          setMapViewState(saving.snapshot.viewState);
          persistedDisplaySettingsRef.current = saving.snapshot.displaySettingsJson;
          setSaveState('saved');
        } else {
          setSaveState('error');
          setObstacleSaveError('Проект сохранён, но временный черновик не удалось обновить в браузере');
        }
        return localDraftSaved;
      } else if (stillCurrentProject) {
        setSaveState('dirty');
        setObstacleSaveError('Во время сохранения появились новые изменения — сохраните ещё раз');
      }
      return false;
    } catch (reason) {
      if (currentProjectIdRef.current === targetProjectId) {
        setSaveState('error');
        setObstacleSaveError(reason instanceof Error ? reason.message : 'Не удалось сохранить проект');
      }
      return false;
    } finally {
      saveInFlightRef.current = false;
      setSaveInFlight(false);
    }
  };

  const selectProject = (project: ProjectSummary, source: 'existing' | 'created'): void => {
    setProjectDialogOpen(false);
    if (source === 'created' && !currentProjectId && saveState !== 'saved') {
      openProjectRequestRef.current += 1;
      currentProjectIdRef.current = project.id;
      setCurrentProjectId(project.id);
      setProjectName(project.name);
      void saveProject(project.id, project.name);
      return;
    }
    if (saveState !== 'saved') {
      setPendingProject(project);
      return;
    }
    void openProject(project);
  };

  const resetWorkspaceAfterLogout = (): void => {
    openProjectRequestRef.current += 1;
    currentProjectIdRef.current = null;
    setCurrentUser(null);
    setDraftReadyUserId(null);
    setProjectName('Новый проект');
    setCurrentProjectId(null);
    setSaveInFlight(false);
    setSites([]);
    setObstacles([]);
    setLinkEndpointIds(['', '']);
    setRadioLinks([]);
    setPtmpGroups([]);
    setSelectedSiteId(null);
    setSelectedSiteAnchor(null);
    setSelectedRadioLinkId(null);
    setActiveTool('select');
    setRadioDraftStartSiteId(null);
    setRadioDraftStaged(false);
    setMapViewState(initialMapViewState);
    setMapResetRevision((revision) => revision + 1);
    setDisplaySettings(defaultDisplaySettings);
    persistedDisplaySettingsRef.current = JSON.stringify(defaultDisplaySettings);
    setCoverageConfig(initialCoverageConfig);
    setRainRateMmPerHour(0);
    setLinkAnalysis(null);
    setResolvedLinkAnalysisKey(null);
    setElevationProfile(null);
    elevationProfileRef.current = null;
    displayedTerrainKeyRef.current = null;
    setTerrainCacheSource(null);
    setDetectedObstacles([]);
    setObstacleWarnings([]);
    setCoverageJob(null);
    setCoverageOverlay(null);
    setInterferenceOverlay(null);
    setClientAnalysis(null);
    setPendingObstacle(null);
    setObstacleSaveError(null);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setHistoryRevision((revision) => revision + 1);
    setSaveState('saved');
  };

  const chooseMapTool = (requested: MapToolId): void => {
    setRadioDraftStartSiteId(null);
    setRadioDraftStaged(false);
    setMapTypeOpen(false);
    setDisplayDrawerOpen(false);
    setActiveTool((current) => nextMapTool(current, requested));
  };

  return (
    <main className="app-shell" onClick={() => { if (contextPoint) setContextPoint(null); if (mapTypeOpen) setMapTypeOpen(false); }}>
      <header className="topbar">
        <div className="brand-mark" aria-hidden="true"><Antenna size={20} /></div>
        <div className="brand-block"><span className="brand-name">Freedom ISP Design</span></div>
        <SearchBox sites={sites} onSelect={(result) => setFocusCoordinates(result.coordinates)} onSelectSite={(site) => { setFocusCoordinates(site.coordinates); selectMapSite(site.id); }} />
        <div className="topbar-right">
          <div className="project-title"><span className="eyebrow">Текущий проект</span><button type="button" onClick={(event) => { event.stopPropagation(); setProjectDialogOpen(true); }}>{projectName} <ChevronDown size={14} /></button></div>
          <div className="header-actions">
            <button className="icon-button" type="button" aria-label="Отменить" title="Отменить последнее изменение" onClick={undo} disabled={undoStackRef.current.length === 0}><Undo2 size={18} /></button>
            <button className="icon-button" type="button" aria-label="Повторить" title="Повторить отменённое изменение" onClick={redo} disabled={redoStackRef.current.length === 0}><Redo2 size={18} /></button>
            <button className="save-button" type="button" onClick={() => void saveProject()} disabled={saveInFlight}><Save size={17} /> {saveInFlight ? 'Сохранение…' : saveState === 'dirty' ? 'Сохранить' : saveState === 'error' ? 'Повторить' : 'Сохранено'}</button>
            <button className="feedback-button" type="button" onClick={(event) => { event.stopPropagation(); if (currentUser) setFeedbackDialogOpen(true); else setAuthDialogOpen(true); }}><Bug size={16} /><span>Сообщить о проблеме</span></button>
            <button className="icon-button" type="button" aria-label="Уведомления"><Bell size={18} /></button>
            <button className="avatar" type="button" aria-label="Профиль пользователя" title={currentUser?.displayName ?? 'Войти'} onClick={(event) => { event.stopPropagation(); setAuthDialogOpen(true); }}>{currentUser ? currentUser.displayName.slice(0, 2).toUpperCase() : 'ВХ'}</button>
          </div>
        </div>
      </header>

      <section className="workspace">
        <MapWorkspace sites={sites} obstacles={obstacles} detectedObstacles={activeLinkAnalysis ? detectedObstacles : []} coverageOverlay={displaySettings.showCoverage ? coverageOverlay : null} interferenceOverlay={interferenceOverlay} coverageBoundary={displaySettings.showCoverage ? coverageBoundary : []} clientStatuses={clientStatuses} radioLinks={radioLinks} selectedRadioLinkId={selectedRadioLinkId} linkQuality={activeLinkAnalysis?.diagnostics.quality ?? null} profileHoverCoordinates={profileHoverCoordinates} selectedSiteId={selectedSiteId} activeTool={activeTool} radioDraftStartSiteId={radioDraftStartSiteId} focusCoordinates={focusCoordinates} viewState={mapViewState} viewResetKey={`workspace:${currentProjectId ?? 'temporary'}:${mapResetRevision}`} displaySettings={displaySettings} command={mapCommand} onViewStateChange={(next) => { const changed = Math.abs(next.center.lat - mapViewState.center.lat) > 1e-7 || Math.abs(next.center.lon - mapViewState.center.lon) > 1e-7 || Math.abs(next.zoom - mapViewState.zoom) > 1e-4 || Math.abs(next.pitch - mapViewState.pitch) > 1e-4 || Math.abs(next.bearing - mapViewState.bearing) > 1e-4; setMapViewState((current) => ({ ...current, ...next })); if (changed && workspaceObjects.length > 0) setSaveState('dirty'); }} onSelectSite={selectMapSite} onSelectRadioLink={selectRadioLink} onSelectedSiteAnchorChange={setSelectedSiteAnchor} onCreateRadio={createRadio} onCreateSite={createSite} onCreateObstacle={(coordinates) => { setActiveTool(cancelMapTool()); setPendingObstacle(coordinates); }} onMoveSite={moveSite} onMoveSiteStart={beginSiteMove} onMoveSiteEnd={endSiteMove} onContextMenu={setContextPoint} onCancelActiveTool={() => { setActiveTool(cancelMapTool()); setRadioDraftStartSiteId(null); setRadioDraftStaged(false); }} onRadioDraftStageChange={setRadioDraftStaged} />

        <nav className="tool-rail" aria-label="Разделы проектирования">
          <MapToolButton definition={selectTool} active={activeTool === 'select'} rail onSelect={chooseMapTool} />
          <button className="rail-action" type="button" aria-label="Топология" onClick={(event) => { event.stopPropagation(); setTopologyDialogOpen(true); }}><Network size={19} /><ControlTooltip label="Топология" description="Открыть связи и объекты проекта" rail /></button>
          <span className="tool-spacer" />
          <button className="rail-action" type="button" aria-label="Каталог оборудования" onClick={(event) => { event.stopPropagation(); setEquipmentDialogTargetSiteId(null); setEquipmentDialogInitialTab('radio'); setEquipmentDialogOpen(true); }}><Settings2 size={19} /><ControlTooltip label="Каталог оборудования" rail /></button>
          <button className="rail-action" type="button" aria-label="Администрирование" onClick={(event) => { event.stopPropagation(); if (currentUser?.role === 'administrator') setAdminDialogOpen(true); else setAuthDialogOpen(true); }}><ShieldCheck size={19} /><ControlTooltip label="Администрирование" rail /></button>
        </nav>

        <div className="map-control-dock" aria-label="Инструменты карты">
          <div className="map-tool-group creation-tools" aria-label="Создание объектов">
            {creationTools.map((definition) => <MapToolButton key={definition.id} definition={definition} active={activeTool === definition.id} onSelect={chooseMapTool} />)}
          </div>
          <div className="map-tool-group navigation-tools" aria-label="Навигация карты">
            <button className="map-tool-button" type="button" aria-label="Приблизить" onClick={(event) => { event.stopPropagation(); setMapCommand({ id: Date.now(), type: 'zoom-in' }); }}><Plus size={17} /><ControlTooltip label="Приблизить" /></button>
            <button className="map-tool-button" type="button" aria-label="Отдалить" onClick={(event) => { event.stopPropagation(); setMapCommand({ id: Date.now(), type: 'zoom-out' }); }}><Minus size={17} /><ControlTooltip label="Отдалить" /></button>
            <button className="map-tool-button" type="button" aria-label="Моё местоположение" onClick={(event) => { event.stopPropagation(); setMapCommand({ id: Date.now(), type: 'locate' }); }}><LocateFixed size={17} /><ControlTooltip label="Моё местоположение" description="Переместить карту к текущей позиции" /></button>
          </div>
          <div className="map-tool-group layer-tools" aria-label="Слои карты">
            <button className={`map-tool-button${mapTypeOpen ? ' active' : ''}`} type="button" aria-label="Тип карты" aria-expanded={mapTypeOpen} onClick={(event) => { event.stopPropagation(); setDisplayDrawerOpen(false); setMapTypeOpen((value) => !value); }}><Layers3 size={17} /><ControlTooltip label="Тип карты" description="Гибрид, здания, рельеф или спутник" /></button>
            <button className={`map-tool-button${displayDrawerOpen && displayDrawerMode === 'coverage' ? ' active' : ''}`} type="button" aria-label="Покрытие" aria-expanded={displayDrawerOpen && displayDrawerMode === 'coverage'} onClick={(event) => { event.stopPropagation(); setMapTypeOpen(false); setDisplayDrawerMode('coverage'); setDisplayDrawerOpen((value) => displayDrawerMode === 'coverage' ? !value : true); }}><CircleDot size={17} /><ControlTooltip label="Покрытие" description="Настройки и расчёт выбранной базовой станции" /></button>
            <button className={`map-tool-button${displayDrawerOpen && displayDrawerMode === 'settings' ? ' active' : ''}`} type="button" aria-label="Параметры отображения" aria-expanded={displayDrawerOpen && displayDrawerMode === 'settings'} onClick={(event) => { event.stopPropagation(); setMapTypeOpen(false); setDisplayDrawerMode('settings'); setDisplayDrawerOpen((value) => displayDrawerMode === 'settings' ? !value : true); }}><SlidersHorizontal size={17} /><ControlTooltip label="Параметры отображения" description="Поверхность, единицы и видимость" /></button>
          </div>
          <div className="map-tool-group secondary-tools" aria-label="Дополнительные инструменты">
            {secondaryTools.map((definition) => <MapToolButton key={definition.id} definition={definition} active={activeTool === definition.id} onSelect={chooseMapTool} />)}
          </div>
        </div>

        {mapTypeOpen ? <div className="map-type-popover" onClick={(event) => event.stopPropagation()}>
          {(['hybrid', 'buildings', 'terrain', 'satellite'] as const).map((style) => <button className={displaySettings.mapStyle === style ? `map-type-card ${style} active` : `map-type-card ${style}`} type="button" key={style} onClick={() => { setDisplaySettings((current) => ({ ...current, mapStyle: style })); setMapTypeOpen(false); setSaveState('dirty'); }}><span />{style === 'hybrid' ? 'Гибрид' : style === 'buildings' ? 'Здания' : style === 'terrain' ? 'Рельеф' : 'Спутник'}</button>)}
        </div> : null}

        <div className="object-counters" aria-label="Счётчики объектов">
          <button type="button" title="Точки доступа"><RadioTower size={14} /><strong>{sites.filter((site) => site.type === 'base_station').length}</strong></button>
          <button type="button" title="Радиолинки"><Antenna size={14} /><strong>{radioLinks.length}</strong></button>
          <button type="button" title="Станции"><Users size={14} /><strong>{sites.filter((site) => site.type === 'client').length}</strong></button>
          <button type="button" title="Препятствия" disabled={obstacles.length === 0}><Layers3 size={14} /><strong>{obstacles.length}</strong></button>
        </div>

        {displayDrawerOpen ? <aside className="display-drawer" onClick={(event) => event.stopPropagation()}>
          <div className="drawer-heading"><div><span className="eyebrow">Карта и радио</span><h2>{displayDrawerMode === 'coverage' ? 'Покрытие' : 'Параметры отображения'}</h2></div><button className="icon-button" type="button" aria-label="Закрыть" onClick={() => setDisplayDrawerOpen(false)}><X size={16} /></button></div>
          <div className="drawer-section"><h3>Покрытие</h3>
            <ToggleSetting label="Показывать покрытие" checked={displaySettings.showCoverage} onChange={(value) => setDisplaySettings((current) => ({ ...current, showCoverage: value }))} />
            <ToggleSetting label="Все диаграммы антенн" checked={displaySettings.showAllAntennas} onChange={(value) => setDisplaySettings((current) => ({ ...current, showAllAntennas: value }))} />
            <ToggleSetting label="Слабый сигнал" checked={displaySettings.showWeakCoverage} onChange={(value) => setDisplaySettings((current) => ({ ...current, showWeakCoverage: value }))} />
            <label className="range-setting"><span>Точность <strong>{displaySettings.coverageAccuracy}%</strong></span><input type="range" min="10" max="100" value={displaySettings.coverageAccuracy} onChange={(event) => setDisplaySettings((current) => ({ ...current, coverageAccuracy: Number(event.target.value) }))} /></label>
            <label className="range-setting"><span>Прозрачность <strong>{displaySettings.coverageOpacity}%</strong></span><input type="range" min="0" max="100" value={displaySettings.coverageOpacity} onChange={(event) => setDisplaySettings((current) => ({ ...current, coverageOpacity: Number(event.target.value) }))} /></label>
            <label className="number-setting"><span>Высота станции по умолчанию</span><input type="number" min="1" max="100" value={displaySettings.defaultStationHeightM} onChange={(event) => setDisplaySettings((current) => ({ ...current, defaultStationHeightM: Math.max(1, Number(event.target.value)) }))} /><b>м</b></label>
            {selectedSite?.type === 'base_station' ? <button className="coverage-action drawer-coverage-action" type="button" onClick={calculateCoverage} disabled={coverageJob?.status === 'sampling' || coverageJob?.status === 'running'}>{coverageJob && coverageJob.status !== 'completed' && coverageJob.status !== 'failed' && coverageJob.status !== 'cancelled' ? `Расчёт покрытия · ${coverageJob.progress}%` : `Рассчитать для ${selectedSite.name}`}</button> : <p className="drawer-note">Выберите базовую станцию на карте, чтобы запустить расчёт покрытия.</p>}
            {coverageError ? <p className="coverage-error">{coverageError}</p> : null}
          </div>
          {displayDrawerMode === 'settings' ? <><div className="drawer-section"><h3>Данные поверхности</h3>
            <ToggleSetting label="Использовать LiDAR" checked={displaySettings.lidarEnabled} onChange={(value) => setDisplaySettings((current) => ({ ...current, lidarEnabled: value }))} />
            <ToggleSetting label="LiDAR для покрытия" checked={displaySettings.lidarCoverage} disabled={!displaySettings.lidarEnabled} onChange={(value) => setDisplaySettings((current) => ({ ...current, lidarCoverage: value }))} />
            <label className="range-setting"><span>Фильтр выбросов <strong>{displaySettings.lidarOutlierPercent}%</strong></span><input type="range" min="0" max="2" step="0.1" disabled={!displaySettings.lidarEnabled} value={displaySettings.lidarOutlierPercent} onChange={(event) => setDisplaySettings((current) => ({ ...current, lidarOutlierPercent: Number(event.target.value) }))} /></label>
          </div>
          <div className="drawer-section"><h3>Слои и единицы</h3>
            <ToggleSetting label="Все станции" checked={displaySettings.showAllStations} onChange={(value) => setDisplaySettings((current) => ({ ...current, showAllStations: value }))} />
            <ToggleSetting label="Карта осадков" checked={displaySettings.rainfallMap} onChange={(value) => setDisplaySettings((current) => ({ ...current, rainfallMap: value }))} />
            <ToggleSetting label="Метеорадары" checked={displaySettings.weatherRadars} onChange={(value) => setDisplaySettings((current) => ({ ...current, weatherRadars: value }))} />
            <div className="segmented"><button className={displaySettings.units === 'metric' ? 'active' : ''} type="button" onClick={() => setDisplaySettings((current) => ({ ...current, units: 'metric' }))}>Metric</button><button className={displaySettings.units === 'imperial' ? 'active' : ''} type="button" onClick={() => setDisplaySettings((current) => ({ ...current, units: 'imperial' }))}>Imperial</button></div>
            <label className="select-setting"><span>Выбор продукта по умолчанию</span><select value={displaySettings.defaultProductSelection} onChange={(event) => setDisplaySettings((current) => ({ ...current, defaultProductSelection: event.target.value as 'auto' | 'manual' }))}><option value="auto">Автоматический</option><option value="manual">Ручной</option></select></label>
          </div></> : null}
        </aside> : null}

        {contextPoint ? (
          <div className="context-menu" style={{ left: contextPoint.x, top: contextPoint.y }} onClick={(event) => event.stopPropagation()}>
            <span>{contextPoint.lat.toFixed(5)}, {contextPoint.lon.toFixed(5)}</span>
            <button type="button" onClick={() => createRadioAtContext('ptmp')}><RadioTower size={15} /> Добавить AP + 2 станции</button>
            <button type="button" onClick={() => createRadioAtContext('ptp')}><Antenna size={15} /> Добавить PtP</button>
            <button type="button" onClick={() => createSite(contextPoint, 'site')}><MapPinPlus size={15} /> Добавить объект</button>
            <button type="button" onClick={() => createSite(contextPoint, 'base_station')}><RadioTower size={15} /> Базовая станция</button>
            <button type="button" onClick={() => createSite(contextPoint, 'client')}><Users size={15} /> Клиент</button>
            <button type="button" onClick={() => { setPendingObstacle(contextPoint); setContextPoint(null); }}><Layers3 size={15} /> Препятствие</button>
          </div>
        ) : null}

        {selectedSite && selectedSiteAnchor ? <SitePopup site={selectedSite} position={selectedSiteAnchor} links={sitePopupLinks} nearbyCount={nearbySiteCount} onSelectLink={selectRadioLink} onPatchSite={(patch) => patchSite(selectedSite.id, patch)} onCreatePtP={() => { setRadioDraftStartSiteId(selectedSite.id); setRadioDraftStaged(true); setSelectedSiteId(null); setSelectedSiteAnchor(null); setActiveTool('ptp'); }} onCreatePtMp={() => { setSelectedSiteId(null); setSelectedSiteAnchor(null); setActiveTool('ptmp'); }} onDelete={deleteSelectedSite} /> : null}

        {selectedRadioLink && linkSites.length === 2 ? profileOpen
          ? <PtPInspector linkName={selectedRadioLink.name} linkKind={selectedRadioLink.kind} endpoints={[linkSites[0]!, linkSites[1]!]} radioRecords={equipmentRadios} antennaRecords={equipmentAntennas} analysis={activeLinkAnalysis} directionalBudgets={directionalBudgets} linkObstructed={linkObstructed} additionalLossDb={selectedRadioLink.additionalLossDb} txPowerLimitsDbm={linkTxPowerLimitsDbm} automaticChannelWidthsMHz={automaticChannelWidthsMHz} manualChannelWidthsMHz={manualChannelWidthsMHz} elevationProfile={activeElevationProfile} terrainCacheSource={activeElevationProfile ? terrainCacheSource : null} loading={analysisBusy} elevationError={elevationError} authenticationRequired={!currentUser} productSelectionMode={selectedRadioLink.productSelectionMode} productBusy={productBusy} recommendationLabel={autoRecommendation ? `${displayEquipmentName(autoRecommendation.deviceModel)} · ${Math.round(autoRecommendation.expectedThroughputMbps)} Мбит/с` : null} rainRateMmPerHour={rainRateMmPerHour} obstacleCount={(activeLinkAnalysis ? detectedObstacles.length : 0) + obstacles.length} obstacleWarnings={activeLinkAnalysis ? obstacleWarnings : []} distanceLabel={formatDistance(linkMetrics.distanceKm * 1_000, displaySettings.units)} bearingAToB={linkMetrics.bearing} onCollapse={() => { setProfileMapHover(null); setProfileOpen(false); }} onRequestAuthentication={() => setAuthDialogOpen(true)} onProductModeChange={setProductSelectionMode} onOpenRadio={(siteId) => { setEquipmentDialogTargetSiteId(siteId); setEquipmentDialogInitialTab('radio'); setEquipmentDialogOpen(true); }} onOpenAntenna={(siteId) => { setEquipmentDialogTargetSiteId(siteId); setEquipmentDialogInitialTab('antenna'); setEquipmentDialogOpen(true); }} onPatchSite={patchSite} onAdditionalLossChange={patchSelectedLinkAdditionalLoss} onProfileHoverChange={changeProfileHover} onEndpointChange={changeProfileEndpoint} onDragStart={beginSiteMove} onDragEnd={endSiteMove} />
          : <button className={`profile-toggle selected-link-strip${linkObstructed ? ' blocked' : ''}`} type="button" aria-label={selectedRadioLink.kind !== 'ptp' ? undefined : linkObstructed ? `Открыть профиль ${selectedRadioLink.name}. Линк перекрыт` : analysisBusy ? `Открыть профиль ${selectedRadioLink.name}. Пропускная способность рассчитывается` : ptpCapacity ? `Открыть профиль ${selectedRadioLink.name}. Пропускная способность: всего ${formatCapacityMbps(ptpCapacity.totalMbps)} Мбит/с; ${linkSites[0]!.name} → ${linkSites[1]!.name}: ${formatCapacityMbps(ptpCapacity.downlinkMbps)} Мбит/с; ${linkSites[1]!.name} → ${linkSites[0]!.name}: ${formatCapacityMbps(ptpCapacity.uplinkMbps)} Мбит/с` : `Открыть профиль ${selectedRadioLink.name}. Пропускная способность недоступна`} onClick={() => setProfileOpen(true)}>
            <span><i />{linkSites[0]?.name ?? 'Station A'}</span>
            <span className="strip-distance">
              <b>←</b>{formatDistance(linkMetrics.distanceKm * 1_000, displaySettings.units)}<b>→</b>
              <span className="strip-capacity-summary"><small>Пропускная способность</small><strong>{linkObstructed ? 'Линк перекрыт' : analysisBusy ? 'Расчёт…' : ptpCapacity ? `${formatCapacityMbps(ptpCapacity.totalMbps)} Мбит/с` : directionalBudgets ? `${linkMetrics.throughput} Мбит/с` : '—'}</strong></span>
              {selectedRadioLink.kind === 'ptp' ? <PtPCapacityTrack breakdown={analysisBusy ? null : ptpCapacity} endpoints={[linkSites[0]!, linkSites[1]!]} blocked={linkObstructed} compact focusable={false} /> : null}
            </span>
            <span><i />{linkSites[1]?.name ?? 'Station B'}</span>
          </button>
          : null}

        {selectedSite && selectedRadioLink && activeLinkAnalysis && coverageJob && clientAnalysis && selectedClientAnalysis && autoRecommendation && activePtMpGroup && heightRecommendation && activeElevationProfile && Boolean(0) ? <aside className={profileOpen ? 'properties-panel' : 'properties-panel profile-closed'}>
          <div className="panel-heading">
            <div><span className="eyebrow">{selectedSite ? 'Выбранный объект' : selectedRadioLink?.kind === 'ptmp' ? 'Выбранный сектор' : 'Выбранная трасса'}</span><h1>{selectedSite ? selectedSite.name : selectedRadioLink?.name ?? 'Радиолинк'}</h1></div>
            <button className="icon-button" type="button" aria-label="Дополнительно"><Ellipsis size={18} /></button>
          </div>
          <div className={`quality-card ${activeLinkAnalysis?.diagnostics.quality ?? ''}`}><div><span className="quality-dot" /> <strong>{selectedSite ? 'Объект активен' : analysisBusy ? activeElevationProfile ? 'Рельеф без изменений' : 'Расчёт рельефа…' : qualityLabel}</strong></div><span>{activeLinkAnalysis && !analysisBusy ? `${Math.round(activeLinkAnalysis.diagnostics.minimumFresnelClearancePercent)}% F1` : 'Расчёт…'}</span></div>
          {selectedSite ? (
            <>
              <div className="stats-grid"><Metric label="Широта" value={selectedSite.coordinates.lat.toFixed(5)} /><Metric label="Долгота" value={selectedSite.coordinates.lon.toFixed(5)} /><Metric label="Высота антенны" value={`${selectedSite.antennaHeightM} м`} accent /><Metric label="Частота" value={`${selectedSite.frequencyGHz} ГГц`} /></div>
              <div className="coordinate-editor"><label><span>Широта</span><input type="number" step="0.000001" value={selectedSite.coordinates.lat} onChange={(event) => moveSite(selectedSite.id, { ...selectedSite.coordinates, lat: Number(event.target.value) })} /></label><label><span>Долгота</span><input type="number" step="0.000001" value={selectedSite.coordinates.lon} onChange={(event) => moveSite(selectedSite.id, { ...selectedSite.coordinates, lon: Number(event.target.value) })} /></label><label><span>Частота, ГГц</span><input type="number" min="0.1" max="100" step="0.1" value={selectedSite.frequencyGHz} onChange={(event) => changeFrequency(Number(event.target.value))} /></label></div>
              <div className="section-title"><span>Параметры объекта</span><span className={selectedSite.type === 'client' ? 'site-badge client' : 'site-badge'}>{selectedSite.type === 'base_station' ? 'БС' : selectedSite.type === 'client' ? 'КЛ' : 'УЗ'}</span></div>
              <div className="field-grid">
                <Field label="Оборудование" value={displayEquipmentName(selectedSite.equipmentName)} wide />
                <Field label="Ширина канала" value={`${selectedSite.channelWidthMHz} МГц`} />
                <Field label="Мощность" value={`${selectedSite.txPowerDbm} dBm`} />
                <Field label="Усиление антенны" value={`${selectedSite.antennaGainDbi} dBi`} />
                <Field label="Потери в кабеле" value={`${selectedSite.cableLossDb} dB`} />
              </div>
              <div className="height-stepper"><span>Высота мачты</span><button type="button" onClick={() => changeHeight(-1)}><Minus size={14} /></button><strong>{selectedSite.antennaHeightM} м</strong><button type="button" onClick={() => changeHeight(1)}><Plus size={14} /></button></div>
              <button className="danger-action" type="button" onClick={deleteSelectedSite}><Trash2 size={14} /> Удалить объект</button>
              {selectedSite.type === 'base_station' ? <><div className="section-title"><span>Сектор PtMP</span><span className="site-badge">terrain-aware</span></div><div className="coverage-controls"><label><span>Макс. радиус, м</span><input type="number" min="100" max="30000" step="100" value={coverageConfig.maxCoverageRadiusM} onChange={(event) => changeCoverageConfig({ maxCoverageRadiusM: Number(event.target.value) })} /></label><label><span>Азимут, °</span><input type="number" min="0" max="359" value={coverageConfig.azimuthDeg} onChange={(event) => changeCoverageConfig({ azimuthDeg: Number(event.target.value) })} /></label><label><span>Ширина H, °</span><input type="number" min="1" max="360" value={coverageConfig.horizontalBeamwidthDeg} onChange={(event) => changeCoverageConfig({ horizontalBeamwidthDeg: Number(event.target.value) })} /></label><label><span>Tilt M/E, °</span><input type="text" value={`${coverageConfig.mechanicalTiltDeg} / ${coverageConfig.electricalTiltDeg}`} onChange={(event) => { const parts = event.target.value.split('/'); const mechanical = Number(parts[0] ?? Number.NaN); const electrical = Number(parts[1] ?? Number.NaN); if (Number.isFinite(mechanical) && Number.isFinite(electrical)) changeCoverageConfig({ mechanicalTiltDeg: mechanical, electricalTiltDeg: electrical }); }} /></label></div><button className="coverage-action" type="button" onClick={calculateCoverage} disabled={coverageJob?.status === 'sampling' || coverageJob?.status === 'running'}>{coverageJob && coverageJob.status !== 'completed' && coverageJob.status !== 'failed' && coverageJob.status !== 'cancelled' ? `Расчёт покрытия · ${coverageJob.progress}%` : 'Рассчитать покрытие'}</button>{coverageError ? <p className="coverage-error">{coverageError}</p> : null}</> : null}
              {selectedSite.type === 'base_station' ? <><div className="section-title"><span>Клиентская доступность</span><span className="site-badge client">{clientAnalysis?.summary.total ?? sites.filter((site) => site.type === 'client').length} клиентов</span></div>{clientAnalysis ? <div className="client-summary"><Metric label="Доступны" value={String(clientAnalysis.summary.available)} accent /><Metric label="Требуют высоты" value={String(clientAnalysis.summary.heightRequired)} /><Metric label="Недоступны" value={String(clientAnalysis.summary.unavailable)} /></div> : null}<button className="client-analysis-action" type="button" onClick={analyzeClients} disabled={clientAnalysisBusy}>{clientAnalysisBusy ? 'Анализ клиентских трасс…' : 'Анализировать клиентов'}</button>{clientAnalysisError ? <p className="coverage-error">{clientAnalysisError}</p> : null}</> : null}
              {selectedSite.type === 'client' && selectedClientAnalysis ? <div className={`client-result ${selectedClientAnalysis.availability}`}><span>{selectedClientAnalysis.availability === 'available' ? 'Доступен' : selectedClientAnalysis.availability === 'height_required' ? 'Требуется высота' : 'Недоступен'}</span><strong>{selectedClientAnalysis.bestAccessPointName ?? 'БС не выбрана'}</strong><small>{selectedClientAnalysis.reasons.join(' · ')}</small>{selectedClientAnalysis.recommendedHeightM ? <button type="button" onClick={applyClientHeight}>Применить {selectedClientAnalysis.recommendedHeightM.toFixed(1)} м</button> : null}</div> : null}
            </>
          ) : (
            <>
              <div className="product-mode-card"><label><input type="checkbox" checked={selectedRadioLink?.productSelectionMode === 'auto'} onChange={(event) => setProductSelectionMode(event.target.checked ? 'auto' : 'manual')} /><span><Sparkles size={14} /><strong>Автоматический подбор</strong><small>{productBusy ? 'Проверяем совместимые продукты…' : autoRecommendation ? `${displayEquipmentName(autoRecommendation.deviceModel)} · ${Math.round(autoRecommendation.expectedThroughputMbps)} Мбит/с` : 'Подбор по длине, частоте, EIRP, запасу и требуемой скорости'}</small></span></label><button type="button" onClick={() => { setEquipmentDialogTargetSiteId(selectedRadioLink?.startSiteId ?? null); setEquipmentDialogInitialTab('radio'); setEquipmentDialogOpen(true); }}>{selectedRadioLink?.productSelectionMode === 'auto' ? 'Все варианты' : 'Выбрать продукт'}</button></div>
              <label className="rainfall-control"><span><CloudRain size={14} /> Осадки <strong>{rainRateMmPerHour === 0 ? 'Нет дождя' : `${rainRateMmPerHour} мм/ч`}</strong></span><input type="range" min="0" max="100" step="1" value={rainRateMmPerHour} onChange={(event) => { setRainRateMmPerHour(Number(event.target.value)); setSaveState('dirty'); }} /></label>
              {activePtMpGroup ? <div className="ptmp-stations"><div className="section-title"><span>Станции {activePtMpGroup.name}</span><span className="site-badge client">Ø {Math.round(averagePtMpCapacity)} Мбит/с</span></div>{ptmpStationRows.map((row) => <button className={row.id === selectedRadioLink?.endSiteId ? 'station-row active' : 'station-row'} type="button" key={row.id} onClick={() => { const link = radioLinks.find((item) => item.groupId === activePtMpGroup.id && item.endSiteId === row.id); if (link) selectRadioLink(link.id); }}><span className="station-number">{row.number}</span><span><strong>{row.site.name}</strong><small>{displayEquipmentName(row.site.equipmentName)}</small></span><span><strong>{formatDistance(row.distanceM, displaySettings.units)}</strong><small>{Math.round(row.capacityMbps)} Мбит/с</small></span></button>)}<button className="add-station" type="button" onClick={addStation}><Plus size={14} /> Добавить станцию</button></div> : null}
              <div className="stats-grid"><Metric label="Расстояние" value={formatDistance(linkMetrics.distanceKm * 1_000, displaySettings.units)} /><Metric label="Азимут A → B" value={`${linkMetrics.bearing.toFixed(1)}°`} /><Metric label="RX signal" value={!linkObstructed && directionalBudgets ? `${Math.round(linkMetrics.rxDbm)} dBm` : '—'} accent /><Metric label="Пропускная способность" value={linkObstructed ? 'Линк перекрыт' : directionalBudgets ? `${linkMetrics.throughput} Мбит/с` : '—'} accent /><Metric label="Fade margin" value={!linkObstructed && directionalBudgets ? `${linkMetrics.fadeMarginDb.toFixed(1)} dB` : '—'} /><Metric label="Модуляция" value={!linkObstructed && directionalBudgets ? linkMetrics.modulation : '—'} /></div>
              {linkSites.map((site) => <div key={site.id}><div className="section-title"><span>Точка {site.id} · {site.name}</span><span className={site.type === 'client' ? 'site-badge client' : 'site-badge'}>{site.type === 'base_station' ? 'БС' : 'КЛ'}</span></div><div className="field-grid"><Field label="Координаты" value={`${site.coordinates.lat.toFixed(5)}, ${site.coordinates.lon.toFixed(5)}`} wide /><Field label="Высота мачты" value={`${site.antennaHeightM} м`} /><Field label="Частота" value={`${site.frequencyGHz} ГГц`} /></div></div>)}
              <div className="section-title"><span>Рекомендация по высоте</span><span className="site-badge">F1 ≥ {optimizationTarget}%</span></div>
              <div className="optimizer-controls">
                <label><span>Режим</span><select value={optimizationMode} onChange={(event) => setOptimizationMode(event.target.value as HeightOptimizationMode)}><option value="a">Только A</option><option value="b">Только B</option><option value="both">Обе поровну</option><option value="minimum_total">Мин. сумма</option></select></label>
                <label><span>Очистка</span><select value={optimizationTarget} onChange={(event) => setOptimizationTarget(Number(event.target.value) as 60 | 100)}><option value="60">60% F1</option><option value="100">100% F1</option></select></label>
              </div>
              <div className={heightRecommendation?.reachable ? 'optimizer-card' : 'optimizer-card unreachable'}>
                {heightRecommendation ? <><div><span>Текущая</span><strong>A {heightRecommendation.currentStartHeightM.toFixed(1)} м · B {heightRecommendation.currentEndHeightM.toFixed(1)} м</strong></div><div><span>Рекомендуется</span><strong>A {heightRecommendation.recommendedStartHeightM.toFixed(1)} м · B {heightRecommendation.recommendedEndHeightM.toFixed(1)} м</strong></div>{heightRecommendation.reachable ? <button type="button" onClick={applyHeightRecommendation}>Применить высоты</button> : <p>{heightRecommendation.reason}</p>}</> : <span>Ожидание профиля высот…</span>}
              </div>
            </>
          )}
          <button className="primary-action" type="button" onClick={() => setSelectedSiteId(null)}>Пересчитать трассу</button>
          <p className="data-source"><Database size={14} /> {elevationProfile ? `${elevationProfile.source} · ~${elevationProfile.resolutionM} м` : elevationError ?? 'Получение данных высот…'} <button type="button">Данные расчёта</button></p>
        </aside> : null}

        {activeTool === 'ptp' ? <div className="workspace-hint radio-tutorial"><span className="tutorial-visual"><i /><i /></span><span><strong>{radioDraftStaged ? 'Укажите вторую точку PtP' : 'Укажите первую точку PtP'}</strong><small>{radioDraftStaged ? 'Двигайте курсор для предпросмотра и щёлкните пустое место или существующий объект. Escape — отмена.' : 'Щёлкните пустое место или существующий объект. После первой точки появится временный линк.'}</small></span><button type="button" aria-label="Отменить создание PtP" onClick={() => chooseMapTool('select')}><X size={14} /></button></div> : null}
        {activeTool === 'ptmp' ? <div className="workspace-hint radio-tutorial"><span className="tutorial-visual sector"><i /><i /><i /></span><span><strong>Разместите точку доступа</strong><small>Щёлкните на карте: появятся AP, две станции, сектор и профиль выбранной станции.</small></span><button type="button" aria-label="Отменить создание PtMP" onClick={() => chooseMapTool('select')}><X size={14} /></button></div> : null}
        {activeTool === 'measure' ? <div className="workspace-hint measure-hint"><Ruler size={15} /><span><strong>Измерение расстояния</strong><small>Укажите две точки. Можно сразу измерить следующий отрезок; Escape — завершить.</small></span><button type="button" aria-label="Завершить измерение" onClick={() => chooseMapTool('select')}><X size={14} /></button></div> : null}
        {coverageOverlay ? <div className="coverage-legend"><span>Сигнал, dBm</span><div className="legend-gradient" /><div className="legend-labels"><span>−45</span><span>−55</span><span>−65</span><span>−75</span><span>−85</span></div><small>{coverageJob?.result ? `${coverageJob.result.resolutionM.toFixed(0)} м/cell · ${coverageJob.result.coveredCellCount} cells` : ''}</small></div> : null}
        {interferenceOverlay ? <div className="interference-legend"><span>Расчётные помехи, dBm</span><div className="interference-gradient" /><div><span>−110</span><span>−100</span><span>−90</span><span>−80</span><span>−70</span></div></div> : null}
        {obstacleSaveError ? <button className="workspace-notice" type="button" onClick={() => setObstacleSaveError(null)}>{obstacleSaveError}<X size={14} /></button> : null}
      </section>
      {authDialogOpen ? <AuthDialog currentUser={currentUser} initialAction={authUrlAction} onActionHandled={() => setAuthUrlAction(null)} onClose={() => { setAuthDialogOpen(false); setAuthUrlAction(null); }} onAuthenticated={setCurrentUser} onLogout={resetWorkspaceAfterLogout} /> : null}
      {feedbackDialogOpen && currentUser ? <FeedbackDialog currentUser={currentUser} onClose={() => setFeedbackDialogOpen(false)} /> : null}
      {projectDialogOpen ? <ProjectDialog onClose={() => setProjectDialogOpen(false)} onSelect={selectProject} /> : null}
      {pendingProject ? <div className="modal-backdrop" role="presentation" onMouseDown={() => setPendingProject(null)}><section className="dialog unsaved-dialog" role="dialog" aria-modal="true" aria-labelledby="unsaved-title" onMouseDown={(event) => event.stopPropagation()}><div className="dialog-heading"><div><span className="eyebrow">Несохранённые изменения</span><h2 id="unsaved-title">Открыть «{pendingProject.name}»?</h2></div><button className="icon-button" type="button" aria-label="Закрыть" onClick={() => setPendingProject(null)}><X size={17} /></button></div><p>{currentProjectId ? 'Текущий проект изменён. Сохраните его, отбросьте изменения или вернитесь к карте.' : 'Сначала сохраните временный черновик как новый проект либо явно отбросьте его.'}</p><div className="unsaved-actions"><button type="button" onClick={() => setPendingProject(null)}>Отмена</button><button className="discard" type="button" onClick={() => { const next = pendingProject; setPendingProject(null); void openProject(next); }}>Не сохранять</button>{currentProjectId ? <button className="primary" type="button" onClick={() => { const next = pendingProject; void saveProject().then((saved) => { if (saved) { setPendingProject(null); void openProject(next); } }); }}>Сохранить и открыть</button> : null}</div></section></div> : null}
      {pendingObstacle ? <ObstacleDialog coordinates={pendingObstacle} onClose={() => setPendingObstacle(null)} onCreate={createObstacle} /> : null}
      {equipmentDialogOpen ? <EquipmentDialog initialTab={equipmentDialogInitialTab} antennaSelectionSites={equipmentTargetSites} selectionContext={equipmentSelectionContext} selectedRadioDeviceId={equipmentSelectionSite?.radioDeviceId ?? null} selectedAntennaModelId={equipmentSelectionSite?.antennaModelId ?? null} linkParameters={selectedRadioLink && linkSites[0] && linkSites[1] ? { frequencyMHz: (equipmentSelectionSite ?? linkSites[0]).frequencyGHz * 1_000, distanceKm: linkMetrics.distanceKm, channelWidthMHz: (equipmentSelectionSite ?? linkSites[0]).channelWidthMHz, requiredThroughputMbps: 100, maxEirpDbm: 36, cableLossDb: (equipmentSelectionSite ?? linkSites[0]).cableLossDb, additionalLossDb: selectedRadioLink.additionalLossDb, ambientNoiseFloorDbm: linkSites[1].ambientNoiseFloorDbm } : null} onApplyRecommendation={applyEquipmentRecommendation} onApplyRadio={applyCatalogRadio} onApplyAntenna={applyCatalogAntenna} onClose={() => { setEquipmentDialogOpen(false); setEquipmentDialogTargetSiteId(null); }} /> : null}
      {topologyDialogOpen ? <TopologyDialog sites={sites} linkEndpointIds={linkEndpointIds} clientAnalysis={clientAnalysis} interference={interferenceAnalysis} noiseLayerVisible={interferenceOverlay !== null} onToggleNoiseLayer={toggleInterferenceLayer} onSelectSite={(siteId) => { setSelectedSiteId(siteId); setTopologyDialogOpen(false); }} onClose={() => setTopologyDialogOpen(false)} /> : null}
      {adminDialogOpen && currentUser ? <AdminDialog currentUserId={currentUser.userId} onClose={() => setAdminDialogOpen(false)} onOpenEquipment={() => { setEquipmentDialogTargetSiteId(null); setEquipmentDialogInitialTab('radio'); setEquipmentDialogOpen(true); }} /> : null}
    </main>
  );
}

function ProfilePanel({ title, distanceKm, elevationProfile, elevationError, analysis, endpoints, loading, rainRateMmPerHour, obstacleCount, obstacleWarnings, onEndpointChange, onDragStart, onDragEnd, onClose }: { title: string; distanceKm: number; elevationProfile: ElevationProfile | null; elevationError: string | null; analysis: LinkEngineeringAnalysis | null; endpoints: [DesignSite, DesignSite] | null; loading: boolean; rainRateMmPerHour: number; obstacleCount: number; obstacleWarnings: string[]; onEndpointChange(endpoint: 'a' | 'b', change: { distanceM?: number; antennaHeightM?: number }): void; onDragStart(): void; onDragEnd(): void; onClose(): void }): React.JSX.Element {
  const elevations = elevationProfile?.samples.map((sample) => sample.elevationM) ?? [];
  const minimum = elevations.length > 0 ? Math.min(...elevations) : 120;
  const maximum = elevations.length > 0 ? Math.max(...elevations) : 220;
  const range = Math.max(1, maximum - minimum);
  const terrainPolygon = elevations.length > 1 ? `polygon(${elevations.map((elevation, index) => `${(index / (elevations.length - 1)) * 100}% ${78 - ((elevation - minimum) / range) * 45}%`).join(', ')}, 100% 100%, 0 100%)` : undefined;
  return (
    <section className="profile-panel" aria-label="Профиль радиотрассы">
      <div className="profile-summary"><div className="profile-title-row"><div><span className="eyebrow">Профиль радиотрассы</span><h2>{title}</h2></div><button className="icon-button" type="button" aria-label="Закрыть профиль" onClick={onClose}><X size={16} /></button></div><div className="profile-kpis"><Metric label="Очистка F1" value={analysis && !loading ? `${Math.round(analysis.diagnostics.minimumFresnelClearancePercent)}%` : '—'} accent /><Metric label="LOS запас" value={analysis && !loading ? `${analysis.diagnostics.minimumLosClearanceM.toFixed(1)} м` : '—'} /><Metric label="Критическая точка" value={analysis && !loading ? `${(analysis.diagnostics.criticalPoint.distanceM / 1_000).toFixed(1)} км` : '—'} /><Metric label="Длина" value={`${distanceKm.toFixed(2)} км`} /></div></div>
      <div className="profile-chart"><ProfileCanvas diagnostics={analysis?.diagnostics ?? null} endpoints={endpoints} loading={loading} onDragStart={onDragStart} onEndpointChange={onEndpointChange} onDragEnd={onDragEnd} />{!analysis ? <><div className="chart-grid" /><div className="terrain-shape" style={terrainPolygon ? { clipPath: terrainPolygon } : undefined} /><div className="fresnel-band" /><div className="los-line" /></> : null}<div className="chart-label top">{Math.round(maximum)} м</div><div className="chart-label bottom">{Math.round(minimum)} м</div>{elevationError ? <div className="profile-error">{elevationError}</div> : null}</div>
      <div className="profile-footer"><span><CloudSun size={15} /> k = 1,33 · {rainRateMmPerHour === 0 ? 'без дождя' : `${rainRateMmPerHour} мм/ч`} · потери {analysis ? `${analysis.rfBudget.rainLossDb.toFixed(1)} dB` : '—'}</span><span><Crosshair size={15} /> {elevationProfile ? `${elevationProfile.samples.length} отсчётов · ${obstacleCount} препятствий` : 'Ожидание DEM'}</span>{analysis?.rfBudget.throughputEstimated ? <span title={analysis.rfBudget.performanceSource}>Пропускная способность — оценка</span> : null}{obstacleWarnings.length > 0 ? <span className="warning-text" title={obstacleWarnings.join('\n')}>Часть источников недоступна</span> : null}<button type="button">Развернуть профиль</button></div>
    </section>
  );
}

function Metric({ label, value, accent = false }: { label: string; value: string; accent?: boolean }): React.JSX.Element {
  return <div className="metric"><span>{label}</span><strong className={accent ? 'accent' : ''}>{value}</strong></div>;
}

function Field({ label, value, wide = false }: { label: string; value: string; wide?: boolean }): React.JSX.Element {
  return <label className={wide ? 'field wide' : 'field'}><span>{label}</span><button type="button">{value}<ChevronDown size={13} /></button></label>;
}

function ToggleSetting({ label, checked, disabled = false, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange(value: boolean): void }): React.JSX.Element {
  return <label className={disabled ? 'toggle-setting disabled' : 'toggle-setting'}><span>{label}</span><input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} /><i aria-hidden="true" /></label>;
}
